import React, { useState, useEffect, useRef, useCallback } from "react";
import ChatWidget from "./ChatWidget";
import { getBackendBaseUrl } from "../../utils/apiUrl";
import {
  FiHome,
  FiCpu,
  FiBookOpen,
  FiBarChart2,
  FiSliders,
  FiCode,
  FiUser,
  FiGlobe,
  FiChevronUp,
  FiChevronDown,
  FiPlus,
  FiTrash2
} from "react-icons/fi";
import "./ChatBotWidget.css";

// Flask Crawler backend server URL.
// Automatically switches between localhost (7001) for local dev and the production server (https://crawler.provaantech.com).
const CRAWLER_BASE_URL =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://127.0.0.1:7001"
    : "https://crawler.provaantech.com";

export default function ChatbotCustomizer({ mode = "agents" }) {
  // Config states
  const [primaryColor, setPrimaryColor] = useState("#6366F1");
  const [agentName, setAgentName] = useState("Ava");
  const [customGreeting, setCustomGreeting] = useState(
    "Hello! I am Ava, your video assistant. I've processed your knowledge documents. How can I help you today?"
  );

  const [systemPrompt, setSystemPrompt] = useState(
    "You are a helpful, professional, and friendly AI assistant. Follow this step-by-step flow:\n1. GREETING: Start by welcoming the user and asking how you can help them today.\n2. CAR QUERY: If the user says they want a car, politely ask what kind of car they are looking for.\n3. SEARCH & OPTIONS: Use the webcrawled website knowledge base to search and present car options. Answer any detailed queries they have based on the crawled website information. If you cannot find the requested information in the webcrawled website knowledge base, you MUST reply with exactly: 'I couldn't find that information in our knowledge base. I don't want to provide inaccurate details. If you'd like, I can arrange for a team member to assist you further.' Do not output verbatim blocks of crawled website text, site headers/footers, or code snippets. However, you are encouraged to answer using the facts, and if the user asks for a website link or URL, you MUST output the clean, raw URL (e.g., https://...) from the documents so it is clickable. When speaking, say 'I've shared the link in the chat' and do not read the URL characters aloud.\n4. SEARCH STRATEGY / FALLBACKS: If the initial search does not return a relevant result for a requested vehicle brand, model, or category, perform a second search using broader keywords, abbreviations, and synonyms (e.g. searching brand and model separately) before concluding the details are missing.\n5. LEAD CAPTURE: Finally, politely ask for their Name, Email, and Phone Number. Remind them: 'Please write this information carefully and accurately so we can ensure there are no mistakes in reaching you.'\n6. DB STORAGE: Once provided, append this tag at the very end of your response to save the lead:\n[LEAD_CAPTURE: {\"name\": \"USER_NAME\", \"email\": \"USER_EMAIL\", \"phone\": \"USER_PHONE\", \"reason\": \"REASON\"}]\n(Replace USER_NAME, USER_EMAIL, USER_PHONE with their details, and REASON with their car preference)."
  );
  const [conversationalContext, setConversationalContext] = useState("");
  const [logo, setLogo] = useState("🤖");
  const [position, setPosition] = useState("right");
  const [replicaId, setReplicaId] = useState("r291e545fd67");
  const [voiceName, setVoiceName] = useState("en-US-AvaMultilingualNeural");
  const [videoHeight, setVideoHeight] = useState(200);
  const [offsetX, setOffsetX] = useState(14);
  const [offsetY, setOffsetY] = useState(14);
  const [widgetHeight, setWidgetHeight] = useState(560);
  const [widgetWidth, setWidgetWidth] = useState(400);
  const [verticalAlign, setVerticalAlign] = useState("bottom");
  const [thumbnailVideoUrl, setThumbnailVideoUrl] = useState("");
  const [voiceButtonText, setVoiceButtonText] = useState("Talk with Avatar");

  // Theme colors
  const [bodyBgColor, setBodyBgColor] = useState("#FFFFFF");
  const [headerBgColor, setHeaderBgColor] = useState("#1E293B");
  const [headerTextColor, setHeaderTextColor] = useState("#FFFFFF");
  const [inputBgColor, setInputBgColor] = useState("#F8FAFC");
  const [textColor, setTextColor] = useState("#1E293B");
  const [minimizedText, setMinimizedText] = useState("Chat with Ava");
  const [minimizedBgColor, setMinimizedBgColor] = useState("#1E293B");
  const [minimizedTextColor, setMinimizedTextColor] = useState("#FFFFFF");
  const [logoBgColor, setLogoBgColor] = useState("#6366F1");
  const [tavusApiKey, setTavusApiKey] = useState("");
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [applyGreenscreen, setApplyGreenscreen] = useState(false);
  const [avatarBackground, setAvatarBackground] = useState("");
  const [avatarBackgroundSize, setAvatarBackgroundSize] = useState("cover");
  const [avatarZoom, setAvatarZoom] = useState(1);
  const [avatarTranslateX, setAvatarTranslateX] = useState(0);
  const [avatarTranslateY, setAvatarTranslateY] = useState(0);
  const [showAvatarPreview, setShowAvatarPreview] = useState(true);
  const [composingText, setComposingText] = useState("is composing response...");
  const [connectingText, setConnectingText] = useState("Launching video connection replica...");
  const [placeholderText, setPlaceholderText] = useState("Type a message...");
  const [joiningText, setJoiningText] = useState("Joining Voice Session...");
  const [sttEngine, setSttEngine] = useState("tavus-auto");
  const [language, setLanguage] = useState("english");
  const [guardrailPrompt, setGuardrailPrompt] = useState("");
  const [guardrailsList, setGuardrailsList] = useState([]);
  const [selectedGuardrailId, setSelectedGuardrailId] = useState("");
  const [newGuardrailName, setNewGuardrailName] = useState("");
  const [newGuardrailPrompt, setNewGuardrailPrompt] = useState("");
  const [isSavingGuardrail, setIsSavingGuardrail] = useState(false);
  const [showGuardrailForm, setShowGuardrailForm] = useState(false);

  // State Management
  const [agentsList, setAgentsList] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("default");
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [saveStatusMsg, setSaveStatusMsg] = useState("");
  const [leadAgentFilter, setLeadAgentFilter] = useState("all");

  const getAgentNameById = (agentId) => {
    if (!agentId || agentId === "default") return "Ava (Default)";
    const agent = agentsList.find(a => a.id === agentId);
    return agent ? agent.name : agentId;
  };

  const [replicas, setReplicas] = useState([]);
  const [searchAvatarQuery, setSearchAvatarQuery] = useState("");

  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [knowledgeType, setKnowledgeType] = useState("avatar"); // 'avatar' | 'text'
  const [textDocs, setTextDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [crawlUrlInput, setCrawlUrlInput] = useState("");
  const [crawlNameInput, setCrawlNameInput] = useState("");
  const [crawling, setCrawling] = useState(false);
  const [crawlError, setCrawlError] = useState("");
  const [crawlDepth, setCrawlDepth] = useState(1);
  const [crawlMaxPages, setCrawlMaxPages] = useState(5);

  const [selectedAgentDocIds, setSelectedAgentDocIds] = useState([]);
  const [selectedAgentCrawlSiteIds, setSelectedAgentCrawlSiteIds] = useState([]);
  const [isPrimary, setIsPrimary] = useState(false);

  const [activeTab, setActiveTab] = useState(mode === "overview" ? "analytics" : "agents"); // 'overview' | 'agents' | 'knowledge' | 'analytics' | 'settings' | 'sdk'
  const [expandedSection, setExpandedSection] = useState("identity"); // accordion expanded state
  const [previewDevice, setPreviewDevice] = useState("desktop"); // 'desktop' | 'mobile'
  const [previewEmbeddedMode, setPreviewEmbeddedMode] = useState(true); // embedded mode vs floating widget mode
  const [copiedSdk, setCopiedSdk] = useState(false);
  const [showMockLanding, setShowMockLanding] = useState(false);
  const [leadsList, setLeadsList] = useState([]);
  const [fetchingLeads, setFetchingLeads] = useState(false);
  const [selectedLeadForModal, setSelectedLeadForModal] = useState(null);
  const [isEditingAgent, setIsEditingAgent] = useState(false);
  const [agentEditSubTab, setAgentEditSubTab] = useState("style"); // 'style' | 'knowledge' | 'analytics' | 'sdk'
  const [activeLeadModalAgent, setActiveLeadModalAgent] = useState(null);
  const [modalLeadsSearch, setModalLeadsSearch] = useState("");

  const handleExportCsv = (agentId, agentName) => {
    const agentLeads = leadsList.filter(l => l.agentId === agentId || (agentId === "default" && !l.agentId));
    if (agentLeads.length === 0) return;

    const headers = ["Name", "Email", "Phone", "Reason/Inquiry", "Date"];
    const rows = agentLeads.map(l => [
      l.name,
      l.email,
      l.phone,
      l.reason ? l.reason.replace(/"/g, '""') : "",
      new Date(l.createdAt).toLocaleDateString()
    ]);

    let csvContent = "data:text/csv;charset=utf-8,"
      + [headers.join(","), ...rows.map(e => e.map(val => `"${val}"`).join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${agentName.replace(/\s+/g, '_')}_Leads.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    setActiveTab(mode === "overview" ? "analytics" : "agents");
    if (mode === "agents") {
      setIsEditingAgent(false);
    }
  }, [mode]);

  const [pastedSdkScript, setPastedSdkScript] = useState("");
  const [loadScriptError, setLoadScriptError] = useState("");
  const [loadScriptSuccess, setLoadScriptSuccess] = useState("");

  const baseUrl = getBackendBaseUrl() + "/chatbot";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id || user.id || "";

  const previewContainerRef = useRef(null);
  const [previewContainerSize, setPreviewContainerSize] = useState({ width: 600, height: 600 });

  const previewContainerCallbackRef = useCallback((node) => {
    if (node !== null) {
      previewContainerRef.current = node;
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setPreviewContainerSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height
          });
        }
      });
      observer.observe(node);
      node.observerDisconnect = () => observer.disconnect();
    } else {
      if (previewContainerRef.current && previewContainerRef.current.observerDisconnect) {
        previewContainerRef.current.observerDisconnect();
      }
      previewContainerRef.current = null;
    }
  }, []);


  const handleCrawlUrlSubmit = async (e) => {
    e.preventDefault();
    if (!crawlUrlInput.trim()) return;

    setCrawling(true);
    setCrawlError("");

    try {
      const token = localStorage.getItem("authToken");
      const targetUrl = knowledgeType === "text"
        ? `${CRAWLER_BASE_URL}/register-site`
        : `${baseUrl}/documents/crawl?agentId=${selectedAgentId}`;

      const headers = {
        "Content-Type": "application/json"
      };
      if (token && knowledgeType !== "text") {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const res = await fetch(targetUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({
          url: crawlUrlInput.trim(),
          name: crawlNameInput.trim() || crawlUrlInput.trim(),
          depth: Number(crawlDepth),
          max_pages: Number(crawlMaxPages)
        })
      });

      if (!res.ok) {
        let errMsg = "Failed to initiate crawl";
        try {
          const errText = await res.text();
          try {
            const errJson = JSON.parse(errText);
            errMsg = errJson.error || errJson.message || errMsg;
          } catch (_) {
            errMsg = errText || errMsg;
          }
        } catch (_) { }
        throw new Error(errMsg);
      }

      const result = await res.json();
      if (knowledgeType === "text") {
        setTextDocs((prev) => [
          ...prev,
          { url: result.url, max_depth: Number(crawlDepth), last_synced: new Date().toISOString(), last_crawl_status: "success" }
        ]);
        setCrawlUrlInput("");
        setCrawlNameInput("");
      } else {
        if (result.success && result.data) {
          setUploadedDocs((prev) => [
            ...prev,
            { document_name: result.data.document_name, document_id: result.data.document_id || result.data.id }
          ]);
          setCrawlUrlInput("");
          setCrawlNameInput("");
        }
      }
    } catch (err) {
      console.error(err);
      setCrawlError(err.message || "Failed to submit web URL crawl request.");
    } finally {
      setCrawling(false);
    }
  };

  const fetchGuardrails = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        const res = await fetch(`${baseUrl}/guardrails`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (res.ok) {
          const json = await res.json();
          if (json && json.success) {
            setGuardrailsList(json.data || []);
          }
        }
      }
    } catch (err) {
      console.warn("Failed to fetch guardrails list:", err);
    }
  };

  // Fetch agents list and uploaded documents on mount
  // Fetch agents list and keys on mount/load
  useEffect(() => {
    fetchGuardrails();
    const fetchAgentsAndKeys = async () => {
      const token = localStorage.getItem("authToken");
      const authHeader = token ? { "Authorization": `Bearer ${token}` } : {};

      try {
        const resAgents = await fetch(`${baseUrl}/agents?userId=${userId}`, {
          headers: authHeader
        });
        if (resAgents.ok) {
          const json = await resAgents.json();
          if (json.success && json.data) {
            setAgentsList(json.data);

            // Automatically select the active primary chatbot agent, or fallback to default on mount
            const primaryAgent = json.data.find(a => a.isPrimary) || json.data.find(a => a.id === "default") || json.data[0];
            if (primaryAgent) {
              handleSelectAgent(primaryAgent);
            }
          }
        }
      } catch (err) {
        console.warn("Failed to fetch agents list:", err);
      }

      try {
        const token = localStorage.getItem("authToken");
        if (token) {
          const res = await fetch(`${baseUrl}/user/keys`, {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          });
          if (res.ok) {
            const json = await res.json();
            if (json.success && json.data) {
              setTavusApiKey(json.data.tavusApiKey || "");
              setOpenaiApiKey(json.data.openaiApiKey || "");
            }
          }
        }
      } catch (err) {
        console.warn("Failed to fetch user API keys:", err);
      }
    };

    fetchAgentsAndKeys();
  }, [baseUrl]);

  // Fetch documents and replicas whenever selectedAgentId changes
  useEffect(() => {
    if (!selectedAgentId || selectedAgentId === "new") return;
    const fetchDocsAndReplicas = async () => {
      const token = localStorage.getItem("authToken");
      const authHeader = token ? { "Authorization": `Bearer ${token}` } : {};

      try {
        const resDocs = await fetch(`${baseUrl}/documents?agentId=${selectedAgentId}`, {
          headers: authHeader
        });
        if (resDocs.ok) {
          const docs = await resDocs.json();
          if (docs.success) {
            setUploadedDocs(docs.data || []);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch documents:", err);
      }

      try {
        const resReplicas = await fetch(`${baseUrl}/replicas?agentId=${selectedAgentId}`, {
          headers: authHeader
        });
        if (resReplicas.ok) {
          const json = await resReplicas.json();
          if (json.success && json.data) {
            setReplicas(json.data);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch replicas list from Tavus:", err);
      }

      try {
        const resTextDocs = await fetch(`${baseUrl}/crawler/registered-sites`, {
          headers: authHeader
        });
        if (resTextDocs.ok) {
          const json = await resTextDocs.json();
          if (json.success && json.data) {
            setTextDocs(json.data.sites || []);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch crawler sites:", err);
      }
    };

    fetchDocsAndReplicas();
  }, [baseUrl, selectedAgentId]);

  useEffect(() => {
    const fetchLeads = async () => {
      setFetchingLeads(true);
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${baseUrl}/leads`, {
          headers: token ? { "Authorization": `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data) {
            setLeadsList(json.data);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch leads list:", err);
      } finally {
        setFetchingLeads(false);
      }
    };
    fetchLeads();
  }, [baseUrl]);

  // Handle Ingest Knowledge PDF
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setUploadError("Please upload a PDF document only.");
      return;
    }

    setUploading(true);
    setUploadError("");

    const formData = new FormData();
    if (knowledgeType === "text") {
      formData.append("file", file);
    } else {
      formData.append("pdf", file);
      formData.append("name", file.name);
    }

    try {
      const token = localStorage.getItem("authToken");
      const targetUrl = knowledgeType === "text"
        ? `${CRAWLER_BASE_URL}/upload-document`
        : `${baseUrl}/documents/upload?agentId=${selectedAgentId}`;

      const res = await fetch(targetUrl, {
        method: "POST",
        headers: (token && knowledgeType !== "text") ? { "Authorization": `Bearer ${token}` } : {},
        body: formData
      });

      if (!res.ok) {
        let errMsg = "Failed to upload document";
        try {
          const errText = await res.text();
          try {
            const errJson = JSON.parse(errText);
            errMsg = errJson.error || errJson.message || errMsg;
          } catch (_) {
            errMsg = errText || errMsg;
          }
        } catch (_) { }
        throw new Error(errMsg);
      }

      const doc = await res.json();
      if (knowledgeType === "text") {
        setTextDocs((prev) => [
          ...prev,
          { url: doc.filename, max_depth: 0, last_synced: new Date().toISOString(), last_crawl_status: "success" }
        ]);
      } else {
        if (doc.success && doc.data) {
          setUploadedDocs((prev) => [
            ...prev,
            { document_name: doc.data.document_name, document_id: doc.data.document_id || doc.data.id }
          ]);
        }
      }
    } catch (err) {
      console.error(err);
      setUploadError(err.message || "Failed to upload PDF. Ensure backend server is running.");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = (id) => {
    setUploadedDocs((prev) => prev.filter((d) => (d.document_id || d.id) !== id));
  };

  const handleDeleteCrawlSite = async (siteId) => {
    if (!window.confirm("Are you sure you want to delete this crawled knowledge base URL and all its indexed data?")) {
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${baseUrl}/crawler/registered-sites/${siteId}`, {
        method: "DELETE",
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
      if (res.ok) {
        setTextDocs((prev) => prev.filter((d) => d.id !== siteId));
      } else {
        const json = await res.json();
        alert(`Failed to delete site: ${json.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Error deleting crawled site:", err);
      alert(`Error deleting crawled site: ${err.message}`);
    }
  };

  // Save Agent Configuration
  const handleSaveAgent = async () => {
    setIsSavingAgent(true);
    setSaveStatusMsg("");
    const payload = {
      id: selectedAgentId,
      name: agentName,
      primaryColor,
      bodyBgColor,
      headerBgColor,
      headerTextColor,
      inputBgColor,
      textColor,
      customGreeting,
      systemPrompt,
      conversationalContext,
      logo,
      position,
      replicaId,
      videoHeight,
      offsetX,
      offsetY,
      documentIds: selectedAgentDocIds,
      crawlSiteIds: selectedAgentCrawlSiteIds,
      isPrimary,
      minimizedText,
      minimizedBgColor,
      minimizedTextColor,
      widgetHeight,
      widgetWidth,
      verticalAlign,
      thumbnailVideoUrl,
      voiceButtonText,
      logoBgColor,
      tavusApiKey,
      userId,
      applyGreenscreen,
      avatarBackground,
      avatarBackgroundSize,
      avatarZoom,
      avatarTranslateX,
      avatarTranslateY,
      showAvatarPreview,
      composingText,
      connectingText,
      placeholderText,
      joiningText,
      sttEngine,
      language,
      guardrailPrompt,
      selectedGuardrailId
    };

    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          await fetch(`${baseUrl}/user/keys`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ tavusApiKey, openaiApiKey })
          });
        } catch (keysErr) {
          console.warn("Failed to save user API keys to backend:", keysErr);
        }
      }

      const res = await fetch(`${baseUrl}/agents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : ""
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          const saved = json.data;
          setSelectedAgentId(saved.id);
          setSaveStatusMsg(`Saved successfully! ID: ${saved.id}`);
          // Refresh list
          const token = localStorage.getItem("authToken");
          const resList = await fetch(`${baseUrl}/agents?userId=${userId}`, {
            headers: token ? { "Authorization": `Bearer ${token}` } : {}
          });
          if (resList.ok) {
            const listJson = await resList.json();
            if (listJson.success) setAgentsList(listJson.data);
          }
        } else {
          setSaveStatusMsg(`Error: ${json.error || "Failed to save"}`);
        }
      } else {
        setSaveStatusMsg("Error connecting to backend API server.");
      }
    } catch (err) {
      console.error("Save Error:", err);
      setSaveStatusMsg("Error: " + err.message);
    } finally {
      setIsSavingAgent(false);
      setTimeout(() => setSaveStatusMsg(""), 4000);
    }
  };

  // Delete Agent Configuration
  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm("Are you sure you want to delete this chatbot agent?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${baseUrl}/agents/${agentId}`, {
        method: "DELETE",
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
      if (res.ok) {
        setSelectedAgentId("default");
        // Refresh list
        const resList = await fetch(`${baseUrl}/agents`);
        if (resList.ok) {
          const listJson = await resList.json();
          if (listJson.success) setAgentsList(listJson.data);
        }
        // Reset states to default Ava config
        setAgentName("Ava");
        setPrimaryColor("#65B30D");
        setBodyBgColor("#FFFFFF");
        setHeaderBgColor("#1E293B");
        setHeaderTextColor("#FFFFFF");
        setInputBgColor("#F8FAFC");
        setTextColor("#1E293B");
        setCustomGreeting("Hello! I am Ava, your video assistant. I've processed your knowledge documents. How can I help you today?");
        setSystemPrompt("You are a helpful, professional, and friendly AI assistant representing Ava. You answer questions based on the uploaded documents. If you cannot find the requested information in the documents or knowledge base, you MUST reply with exactly: 'I couldn't find that information in our knowledge base. I don't want to provide inaccurate details. If you'd like, I can arrange for a team member to assist you further.' Do not output verbatim blocks of crawled website text, site headers/footers, or code snippets. However, you are encouraged to answer using the facts, and if the user asks for a website link or URL, you MUST output the clean, raw URL (e.g., https://...) from the documents so it is clickable. When speaking, say 'I've shared the link in the chat' and do not read the URL characters aloud.");
        setConversationalContext("");
        setLogo("💬");
        setReplicaId("r291e545fd67");
        setSelectedAgentDocIds([]);
        setIsPrimary(false);
        setMinimizedText("Chat with Ava");
        setMinimizedBgColor("#1E293B");
        setMinimizedTextColor("#FFFFFF");
        setLogoBgColor("#6366F1");
        setTavusApiKey("");
        setWidgetHeight(560);
        setWidgetWidth(400);
        setVerticalAlign("bottom");
        setThumbnailVideoUrl("");
        setVoiceButtonText("Talk with Avatar");
        setApplyGreenscreen(false);
        setAvatarBackground("");
        setAvatarBackgroundSize("cover");
        setAvatarZoom(1);
        setAvatarTranslateX(0);
        setAvatarTranslateY(0);
        setShowAvatarPreview(true);
        setComposingText("is composing response...");
        setConnectingText("Launching video connection replica...");
        setPlaceholderText("Type a message...");
        setJoiningText("Joining Voice Session...");
        setSttEngine("tavus-auto");
        setLanguage("english");
        setGuardrailPrompt("");
        setSelectedGuardrailId("");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      alert("Failed to delete agent: " + err.message);
    }
  };

  function handleSelectAgent(agent) {
    setSelectedAgentId(agent.id);
    setAgentName(agent.name || "Ava");
    setPrimaryColor(agent.primaryColor || "#65B30D");
    setBodyBgColor(agent.bodyBgColor || "#FFFFFF");
    setHeaderBgColor(agent.headerBgColor || "#1E293B");
    setHeaderTextColor(agent.headerTextColor || "#FFFFFF");
    setInputBgColor(agent.inputBgColor || "#F8FAFC");
    setTextColor(agent.textColor || "#1E293B");
    setCustomGreeting(agent.customGreeting || "");
    setSystemPrompt(agent.systemPrompt || "");
    setConversationalContext(agent.conversationalContext || "");
    setLogo(agent.logo || "🤖");
    setPosition(agent.position || "right");
    setReplicaId(agent.replicaId || "r291e545fd67");
    if (agent.videoHeight) setVideoHeight(Number(agent.videoHeight));
    if (agent.offsetX) setOffsetX(Number(agent.offsetX));
    if (agent.offsetY) setOffsetY(Number(agent.offsetY));
    setSelectedAgentDocIds(agent.documentIds || []);
    setSelectedAgentCrawlSiteIds(agent.crawlSiteIds || []);
    setIsPrimary(agent.isPrimary || false);
    setMinimizedText(agent.minimizedText || "Chat with " + (agent.name || "Ava"));
    setMinimizedBgColor(agent.minimizedBgColor || agent.headerBgColor || "#1E293B");
    setMinimizedTextColor(agent.minimizedTextColor || agent.headerTextColor || "#FFFFFF");
    setLogoBgColor(agent.logoBgColor || agent.primaryColor || "#6366F1");
    if (agent.tavusApiKey) setTavusApiKey(agent.tavusApiKey);
    setWidgetHeight(agent.widgetHeight || 560);
    setWidgetWidth(agent.widgetWidth || 400);
    setVerticalAlign(agent.verticalAlign || "bottom");
    setThumbnailVideoUrl(agent.thumbnailVideoUrl || "");
    setVoiceButtonText(agent.voiceButtonText || "Talk with Avatar");
    setApplyGreenscreen(agent.applyGreenscreen || false);
    setAvatarBackground(agent.avatarBackground || "");
    setAvatarBackgroundSize(agent.avatarBackgroundSize || "cover");
    setAvatarZoom(agent.avatarZoom !== undefined ? Number(agent.avatarZoom) : 1);
    setAvatarTranslateX(agent.avatarTranslateX !== undefined ? Number(agent.avatarTranslateX) : 0);
    setAvatarTranslateY(agent.avatarTranslateY !== undefined ? Number(agent.avatarTranslateY) : 0);
    setShowAvatarPreview(agent.showAvatarPreview !== undefined ? (agent.showAvatarPreview === "true" || agent.showAvatarPreview === true) : true);
    setComposingText(agent.composingText || "is composing response...");
    setConnectingText(agent.connectingText || "Launching video connection replica...");
    setPlaceholderText(agent.placeholderText || "Type a message...");
    setJoiningText(agent.joiningText || "Joining Voice Session...");
    setSttEngine(agent.sttEngine || "tavus-auto");
    setLanguage(agent.language || "english");
    setGuardrailPrompt(agent.guardrailPrompt || "");
    setSelectedGuardrailId(agent.selectedGuardrailId || "");
  };

  const handleLoadFromScript = () => {
    setLoadScriptError("");
    setLoadScriptSuccess("");
    if (!pastedSdkScript.trim()) return;

    // Regex to capture data-agent-id from pasted script tag
    const agentIdRegex = /data-agent-id=["']([^"']+)["']/;
    const match = pastedSdkScript.match(agentIdRegex);
    if (!match || !match[1]) {
      setLoadScriptError("Could not extract data-agent-id from the pasted script tag. Ensure it has a valid 'data-agent-id' attribute.");
      return;
    }

    const agentId = match[1];
    const foundAgent = agentsList.find(a => a.id === agentId);
    if (foundAgent) {
      handleSelectAgent(foundAgent);
      setLoadScriptSuccess(`Successfully loaded chatbot "${foundAgent.name}" (ID: ${agentId})!`);
      setPastedSdkScript("");
    } else {
      // Try to fetch from server directly if not in currently loaded agents list
      fetch(`${baseUrl}/agents/${agentId}`)
        .then(res => res.json())
        .then(json => {
          if (json.success && json.data) {
            handleSelectAgent(json.data);
            setLoadScriptSuccess(`Successfully fetched and loaded chatbot "${json.data.name}" (ID: ${agentId})!`);
            // Append to list so it renders in sidebar
            setAgentsList(prev => [json.data, ...prev]);
            setPastedSdkScript("");
          } else {
            setLoadScriptError(`Chatbot with ID "${agentId}" was not found in the system database.`);
          }
        })
        .catch(err => {
          setLoadScriptError(`Error loading chatbot from database: ${err.message}`);
        });
    }
  };

  const handleCreateNewAgent = () => {
    setSelectedAgentId("new");
    setAgentName("New Chatbot");
    setPrimaryColor("#6366F1");
    setBodyBgColor("#FFFFFF");
    setHeaderBgColor("#1E293B");
    setHeaderTextColor("#FFFFFF");
    setInputBgColor("#F8FAFC");
    setTextColor("#1E293B");
    setCustomGreeting("Hello! I am your custom assistant. How can I help you today?");
    setSystemPrompt("You are a helpful, professional AI assistant. If you cannot find the requested information in the documents or knowledge base, you MUST reply with exactly: 'I couldn't find that information in our knowledge base. I don't want to provide inaccurate details. If you'd like, I can arrange for a team member to assist you further.' Do not output verbatim blocks of crawled website text, site headers/footers, or code snippets. However, you are encouraged to answer using the facts, and if the user asks for a website link or URL, you MUST output the clean, raw URL (e.g., https://...) from the documents so it is clickable. When speaking, say 'I've shared the link in the chat' and do not read the URL characters aloud.");
    setConversationalContext("");
    setLogo("🤖");
    setReplicaId("r291e545fd67");
    setSelectedAgentDocIds([]);
    setSelectedAgentCrawlSiteIds([]);
    setIsPrimary(false);
    setMinimizedText("Chat with New Chatbot");
    setMinimizedBgColor("#1E293B");
    setMinimizedTextColor("#FFFFFF");
    setLogoBgColor("#6366F1");
    setTavusApiKey("");
    setWidgetHeight(560);
    setWidgetWidth(400);
    setVerticalAlign("bottom");
    setApplyGreenscreen(false);
    setAvatarBackground("");
    setAvatarBackgroundSize("cover");
    setAvatarZoom(1);
    setAvatarTranslateX(0);
    setAvatarTranslateY(0);
    setShowAvatarPreview(true);
    setThumbnailVideoUrl("");
    setVoiceButtonText("Talk with Avatar");
    setComposingText("is composing response...");
    setConnectingText("Launching video connection replica...");
    setPlaceholderText("Type a message...");
    setJoiningText("Joining Voice Session...");
    setGuardrailPrompt("");
    setSelectedGuardrailId("");
  };

  return (
    <div className="customizer-workspace">
      {/* View Router */}
      <div className="customizer-content-wrapper">

        {/* AGENTS LAB TAB */}
        {activeTab === "agents" && (
          !isEditingAgent ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%", height: "100%", overflowY: "auto", paddingRight: "10px", animation: "fadeIn 0.2s ease", paddingTop: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {/* <h2 style={{ fontSize: "1.4rem", fontWeight: "800", color: "#1E293B", margin: 0 }}>Chatbot Agents</h2> */}
                {/* <button
                  onClick={() => {
                    handleCreateNewAgent();
                    setIsEditingAgent(true);
                  }}
                  style={{
                    background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "12px",
                    padding: "10px 20px",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                    transition: "all 0.2s"
                  }}
                >
                  ➕ Create New Agent
                </button> */}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(235px, 1fr))", gap: "16px", width: "100%" }}>
                {/* Create New Agent Card */}
                <div
                  onClick={() => {
                    handleCreateNewAgent();
                    setIsEditingAgent(true);
                  }}
                  style={{
                    background: "#FFFFFF",
                    border: "2px dashed #CBD5E1",
                    borderRadius: "12px",
                    padding: "16px 12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    cursor: "pointer",
                    minHeight: "155px",
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.02)"
                  }}
                  className="create-agent-card-hover"
                >
                  <span style={{ fontSize: "1.8rem" }}>➕</span>
                  <div style={{ fontWeight: "700", color: "#6366F1", fontSize: "0.95rem" }}>Create New Agent</div>
                  <div style={{ fontSize: "0.74rem", color: "#64748B", textAlign: "center", lineHeight: "1.35" }}>Create and customize a new chatbot assistant with avatar options.</div>
                </div>

                {/* Existing Agents Cards */}
                {agentsList.map((agent) => {
                  const agentLeads = leadsList.filter(l => l.agentId === agent.id || (agent.id === "default" && !l.agentId));
                  return (
                    <div
                      key={agent.id}
                      onClick={() => {
                        handleSelectAgent(agent);
                        setIsEditingAgent(true);
                      }}
                      style={{
                        background: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "18px 12px 14px 12px",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.02)",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: "6px",
                        minHeight: "155px"
                      }}
                      className="agent-card-hover"
                    >
                      {/* Delete Icon Button (Top Left) */}
                      <div style={{ position: "absolute", top: "12px", left: "12px" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAgent(agent.id);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#94A3B8",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            transition: "all 0.2s"
                          }}
                          className="delete-icon-btn-hover"
                          title="Delete Chatbot"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>

                      {/* Leads Count Action Badge (Top Right) */}
                      <div style={{ position: "absolute", top: "12px", right: "12px" }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveLeadModalAgent(agent);
                          }}
                          style={{
                            background: "rgba(99, 102, 241, 0.08)",
                            border: "none",
                            borderRadius: "5px",
                            padding: "3px 8px",
                            color: "#6366F1",
                            fontSize: "0.68rem",
                            fontWeight: "700",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px"
                          }}
                          className="leads-badge-btn"
                          title="Click to view leads"
                        >
                          📊 {agentLeads.length}
                        </button>
                      </div>

                      {/* Centered Avatar */}
                      <span style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        background: agent.primaryColor || "#6366F1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                        color: "#fff",
                        boxShadow: `0 2px 6px ${(agent.primaryColor || "#6366F1")}20`,
                        position: "relative",
                        flexShrink: 0,
                        marginTop: "8px"
                      }}>
                        {agent.logo && agent.logo.startsWith("http") ? (
                          <img src={agent.logo} alt="Logo" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", border: "none" }} />
                        ) : (
                          agent.logo || "🤖"
                        )}
                        {agent.isPrimary && (
                          <span style={{
                            position: "absolute",
                            bottom: "-2px",
                            right: "-2px",
                            background: "#10B981",
                            color: "#FFFFFF",
                            borderRadius: "50%",
                            width: "14px",
                            height: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.55rem",
                            fontWeight: "bold",
                            border: "1.5px solid #FFFFFF",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.15)"
                          }} title="Active Primary Agent">
                            ✓
                          </span>
                        )}
                      </span>

                      {/* Centered Name Info */}
                      <div style={{ fontWeight: "700", color: "#1E293B", fontSize: "0.95rem", marginTop: "2px", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {agent.name}
                      </div>

                      {/* Centered Snippet Text */}
                      <div style={{ fontSize: "0.74rem", color: "#64748B", lineHeight: "1.35", flex: 1, maxWidth: "95%", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: "34px" }}>
                        {agent.customGreeting ? (agent.customGreeting.substring(0, 75) + (agent.customGreeting.length > 75 ? "..." : "")) : "No greeting configured."}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="customizer-container-premium">
              <div className="editor-panel-premium">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <button
                    onClick={() => setIsEditingAgent(false)}
                    style={{
                      background: "#F1F5F9",
                      color: "#475569",
                      border: "none",
                      borderRadius: "10px",
                      padding: "10px 16px",
                      fontSize: "0.85rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "all 0.2s"
                    }}
                  >
                    ← Back to Grid
                  </button>
                  <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#64748B" }}>
                    Active Agent: <strong style={{ color: "#1E293B" }}>{agentName}</strong> (ID: {selectedAgentId})
                  </span>
                </div>

                {/* Sub Navigation Tabs */}
                <div style={{ display: "flex", gap: "8px", borderBottom: "1.5px solid #F1F5F9", paddingBottom: "12px", marginBottom: "12px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => setAgentEditSubTab("style")}
                    style={{
                      background: agentEditSubTab === "style" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#F1F5F9",
                      color: agentEditSubTab === "style" ? "#FFFFFF" : "#475569",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    🎨 Theme & Styling
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgentEditSubTab("knowledge")}
                    style={{
                      background: agentEditSubTab === "knowledge" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#F1F5F9",
                      color: agentEditSubTab === "knowledge" ? "#FFFFFF" : "#475569",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    📚 Ingest Knowledge
                  </button>
                  <button
                    type="button"
                    onClick={() => setAgentEditSubTab("sdk")}
                    style={{
                      background: agentEditSubTab === "sdk" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#F1F5F9",
                      color: agentEditSubTab === "sdk" ? "#FFFFFF" : "#475569",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    🔌 Embed SDK script
                  </button>
                </div>

                {/* Sub Tab Panel 1: Theme & Styling (Original Forms) */}
                {agentEditSubTab === "style" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    <h3 style={{ color: "#1E293B", fontSize: "1.1rem", margin: "0 0 10px 0" }}>Configure Agent Persona</h3>

                    {/* Accordion 1: Identity & Persona */}
                    <div className={`accordion-section ${expandedSection === "identity" ? "expanded" : ""}`}>
                      <button
                        type="button"
                        className="accordion-header"
                        onClick={() => setExpandedSection(expandedSection === "identity" ? "" : "identity")}
                      >
                        <span>🤖 Persona & Identity</span>
                        <span>{expandedSection === "identity" ? "▼" : "▲"}</span>
                      </button>
                      {expandedSection === "identity" && (
                        <div className="accordion-body">
                          <div className="custom-form-group">
                            <label className="custom-form-label">Agent Name</label>
                            <input type="text" value={agentName} onChange={(e) => setAgentName(e.target.value)} className="custom-form-input" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Agent ID (Unique)</label>
                            <input type="text" value={selectedAgentId} readOnly className="custom-form-input" style={{ background: "rgba(255, 255, 255, 0.05)", color: "#64748B", cursor: "not-allowed" }} />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Greeting Message</label>
                            <input type="text" value={customGreeting} onChange={(e) => setCustomGreeting(e.target.value)} className="custom-form-input" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Voice Button Text</label>
                            <input type="text" value={voiceButtonText} onChange={(e) => setVoiceButtonText(e.target.value)} className="custom-form-input" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Chatbot Logo (Text/Emoji or Upload Image)</label>
                            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                              <input
                                type="text"
                                placeholder="Enter emoji or text..."
                                value={logo}
                                onChange={(e) => setLogo(e.target.value)}
                                className="custom-form-input"
                                style={{ flex: 1 }}
                              />
                              <span style={{ fontSize: "0.85rem", color: "#64748B" }}>or</span>
                              <input
                                type="file"
                                accept="image/*"
                                id="logoImageUpload"
                                style={{ display: "none" }}
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  try {
                                    const token = localStorage.getItem("authToken");
                                    const formData = new FormData();
                                    formData.append("logo", file);

                                    const res = await fetch(`${baseUrl}/agents/logo/upload`, {
                                      method: "POST",
                                      headers: {
                                        Authorization: token ? `Bearer ${token}` : ""
                                      },
                                      body: formData
                                    });
                                    const json = await res.json();
                                    if (json.success && json.data && json.data.logoUrl) {
                                      setLogo(json.data.logoUrl);
                                      alert("Logo image uploaded successfully!");
                                    } else {
                                      console.error("Upload response failure:", json);
                                      alert("Failed to upload image: " + (json.error || json.message || "Unknown error"));
                                    }
                                  } catch (err) {
                                    console.error("Upload network error:", err);
                                    alert("Error uploading logo image: " + err.message);
                                  } finally {
                                    e.target.value = "";
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => document.getElementById("logoImageUpload").click()}
                                style={{
                                  background: "rgba(99, 102, 241, 0.15)",
                                  color: "#818CF8",
                                  border: "1px solid rgba(99, 102, 241, 0.3)",
                                  borderRadius: "8px",
                                  padding: "8px 14px",
                                  fontSize: "0.82rem",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                📸 Upload Logo
                              </button>
                            </div>
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span>Select Video Avatar (from Tavus)</span>
                              {replicas.length > 0 && <span style={{ fontSize: "0.75rem", color: "#6366F1", fontWeight: "600" }}>{replicas.length} Available</span>}
                            </label>
                            <input
                              type="text"
                              placeholder="🔍 Search replicas/avatars..."
                              value={searchAvatarQuery}
                              onChange={(e) => setSearchAvatarQuery(e.target.value)}
                              className="custom-form-input"
                              style={{ marginBottom: "10px", padding: "8px 12px", fontSize: "0.8rem" }}
                            />
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(3, 1fr)",
                              gap: "12px",
                              maxHeight: "220px",
                              overflowY: "auto",
                              background: "#F8FAFC",
                              padding: "12px",
                              borderRadius: "12px",
                              border: "1px solid #E2E8F0"
                            }}>
                              {replicas.length === 0 ? (
                                <div style={{ gridColumn: "span 3", color: "#64748B", fontSize: "0.8rem", fontStyle: "italic", textAlign: "center", padding: "16px" }}>
                                  No avatars found or loading...
                                </div>
                              ) : (
                                replicas
                                  .filter(av => {
                                    const name = av.replica_name || "";
                                    const id = av.replica_id || "";
                                    return name.toLowerCase().includes(searchAvatarQuery.toLowerCase()) || id.toLowerCase().includes(searchAvatarQuery.toLowerCase());
                                  })
                                  .map((av) => (
                                    <div
                                      key={av.replica_id}
                                      onClick={() => {
                                        setReplicaId(av.replica_id);
                                        setAgentName(av.replica_name || "Ava");
                                        setThumbnailVideoUrl(av.thumbnail_video_url || "");
                                      }}
                                      style={{
                                        background: "#FFFFFF",
                                        border: replicaId === av.replica_id ? `2.5px solid ${primaryColor}` : "1.5px solid #E2E8F0",
                                        borderRadius: "12px",
                                        padding: "10px",
                                        cursor: "pointer",
                                        textAlign: "center",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: "6px",
                                        transition: "all 0.15s ease-in-out",
                                        boxShadow: replicaId === av.replica_id ? "0 4px 12px rgba(99, 102, 241, 0.12)" : "none"
                                      }}
                                    >
                                      {av.thumbnail_image_url ? (
                                        <img
                                          src={av.thumbnail_image_url}
                                          alt={av.replica_name}
                                          style={{ width: "42px", height: "42px", borderRadius: "50%", objectFit: "cover", border: "1px solid #E2E8F0" }}
                                        />
                                      ) : (
                                        <span style={{ fontSize: "1.8rem" }}>💬</span>
                                      )}
                                      <div style={{ fontSize: "0.72rem", fontWeight: "600", color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                                        {av.replica_name || "Unnamed"}
                                      </div>
                                    </div>
                                  ))
                              )}
                            </div>
                          </div>
                          <div className="custom-form-group" style={{ marginTop: "14px" }}>
                            <label className="custom-form-label">Speech-to-Text Accent Engine</label>
                            <select value={sttEngine} onChange={(e) => setSttEngine(e.target.value)} className="custom-form-select">
                              <option value="tavus-auto">Auto Detect Accent (Recommended)</option>
                              <option value="tavus-parakeet">US/UK/European Accent (Parakeet)</option>
                              <option value="tavus-soniox">Indian Accent (Soniox)</option>
                              <option value="tavus-whisper">Noisy Environments / Multilingual (Whisper)</option>
                            </select>
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Preferred Spoken Language</label>
                            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="custom-form-select">
                              <option value="english">English</option>
                              <option value="hindi">Hindi</option>
                              <option value="spanish">Spanish</option>
                              <option value="french">French</option>
                              <option value="german">German</option>
                              <option value="italian">Italian</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Accordion 2: Knowledge & Instructions */}
                    <div className={`accordion-section ${expandedSection === "knowledge" ? "expanded" : ""}`}>
                      <button
                        type="button"
                        className="accordion-header"
                        onClick={() => setExpandedSection(expandedSection === "knowledge" ? "" : "knowledge")}
                      >
                        <span>📚 Knowledge & Instructions</span>
                        <span>{expandedSection === "knowledge" ? "▼" : "▲"}</span>
                      </button>
                      {expandedSection === "knowledge" && (
                        <div className="accordion-body">
                          <div className="custom-form-group">
                            <label className="custom-form-label">System Instructions</label>
                            <textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} className="custom-form-textarea" rows={6} />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Attach Knowledge Documents</label>
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              background: "#F8FAFC",
                              padding: "12px",
                              borderRadius: "12px",
                              border: "1px solid #E2E8F0",
                              maxHeight: "150px",
                              overflowY: "auto"
                            }}>
                              {uploadedDocs.length === 0 ? (
                                <div style={{ color: "#64748B", fontSize: "0.8rem", fontStyle: "italic" }}>No knowledge documents found. Ingest some first in the "Ingest Knowledge" tab.</div>
                              ) : (
                                uploadedDocs.map((doc) => {
                                  const docId = doc.document_id || doc.id;
                                  const isChecked = selectedAgentDocIds.includes(docId);
                                  return (
                                    <label key={docId} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", color: "#1E293B", cursor: "pointer" }}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedAgentDocIds((prev) => [...prev, docId]);
                                          } else {
                                            setSelectedAgentDocIds((prev) => prev.filter((id) => id !== docId));
                                          }
                                        }}
                                        style={{ accentColor: primaryColor }}
                                      />
                                      <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", flex: 1 }}>
                                        {doc.document_name}
                                      </span>
                                      {doc.status && (
                                        <span style={{
                                          fontSize: "0.7rem",
                                          padding: "2px 6px",
                                          borderRadius: "4px",
                                          background: doc.status === "ready" ? "#DCFCE7" : (doc.status === "failed" ? "#FEE2E2" : "#FEF9C3"),
                                          color: doc.status === "ready" ? "#166534" : (doc.status === "failed" ? "#991B1B" : "#854D0E"),
                                          marginLeft: "auto"
                                        }}>
                                          {doc.status}
                                        </span>
                                      )}
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          <div className="custom-form-group" style={{ marginTop: "16px" }}>
                            <label className="custom-form-label">Attach Crawled Website Knowledge Base</label>
                            <div style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              background: "#F8FAFC",
                              padding: "12px",
                              borderRadius: "12px",
                              border: "1px solid #E2E8F0",
                              maxHeight: "150px",
                              overflowY: "auto"
                            }}>
                              {textDocs.length === 0 ? (
                                <div style={{ color: "#64748B", fontSize: "0.8rem", fontStyle: "italic" }}>No crawled websites found. Ingest some first in the "Ingest Knowledge" tab.</div>
                              ) : (
                                textDocs.map((doc) => {
                                  const siteId = String(doc.id);
                                  const isChecked = selectedAgentCrawlSiteIds.includes(siteId);
                                  return (
                                    <label key={siteId} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", color: "#1E293B", cursor: "pointer" }}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedAgentCrawlSiteIds((prev) => [...prev, siteId]);
                                          } else {
                                            setSelectedAgentCrawlSiteIds((prev) => prev.filter((id) => id !== siteId));
                                          }
                                        }}
                                        style={{ accentColor: primaryColor }}
                                      />
                                      <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", flex: 1 }} title={doc.url}>
                                        🌐 {doc.url}
                                      </span>
                                      {doc.last_crawl_status && (
                                        <span style={{
                                          fontSize: "0.7rem",
                                          padding: "2px 6px",
                                          borderRadius: "4px",
                                          background: doc.last_crawl_status === "success" ? "#DCFCE7" : (doc.last_crawl_status === "failed" ? "#FEE2E2" : "#FEF9C3"),
                                          color: doc.last_crawl_status === "success" ? "#166534" : (doc.last_crawl_status === "failed" ? "#991B1B" : "#854D0E"),
                                          marginLeft: "auto"
                                        }}>
                                          {doc.last_crawl_status}
                                        </span>
                                      )}
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          <div className="custom-form-group" style={{ marginTop: "16px" }}>
                            <label className="custom-form-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span>🛡️ Safety Guardrail Template</span>
                              <button
                                type="button"
                                style={{ background: "none", border: "none", color: primaryColor, cursor: "pointer", fontSize: "0.8rem", fontWeight: "bold" }}
                                onClick={() => setShowGuardrailForm(!showGuardrailForm)}
                              >
                                {showGuardrailForm ? "✕ Cancel" : "+ Create New Template"}
                              </button>
                            </label>

                            {!showGuardrailForm && (
                              <>
                                <select
                                  value={selectedGuardrailId}
                                  onChange={(e) => setSelectedGuardrailId(e.target.value)}
                                  className="custom-form-select"
                                >
                                  <option value="">-- No Guardrail Template (None) --</option>
                                  {guardrailsList.map((g) => (
                                    <option key={g.id} value={g.id}>
                                      {g.name}
                                    </option>
                                  ))}
                                </select>

                                {selectedGuardrailId && (
                                  <div style={{ marginTop: "10px", background: "#F1F5F9", padding: "10px", borderRadius: "8px", fontSize: "0.8rem", borderLeft: `3px solid ${primaryColor}` }}>
                                    <strong>Rules Summary:</strong>
                                    <div style={{ color: "#475569", marginTop: "4px", whiteSpace: "pre-wrap" }}>
                                      {guardrailsList.find(g => g.id === selectedGuardrailId)?.prompt}
                                    </div>
                                    <button
                                      type="button"
                                      style={{ marginTop: "8px", background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "0.75rem", padding: 0 }}
                                      onClick={async () => {
                                        if (window.confirm("Are you sure you want to delete this guardrail template from the library?")) {
                                          try {
                                            const token = localStorage.getItem("authToken");
                                            const res = await fetch(`${baseUrl}/guardrails/${selectedGuardrailId}`, {
                                              method: "DELETE",
                                              headers: token ? { "Authorization": `Bearer ${token}` } : {}
                                            });
                                            if (res.ok) {
                                              setSelectedGuardrailId("");
                                              fetchGuardrails();
                                            }
                                          } catch (err) {
                                            console.error(err);
                                          }
                                        }
                                      }}
                                    >
                                      🗑️ Delete Template from Library
                                    </button>
                                  </div>
                                )}

                                {!selectedGuardrailId && (
                                  <div style={{ marginTop: "12px" }}>
                                    <label className="custom-form-label" style={{ fontSize: "0.8rem", color: "#64748B" }}>Or define inline custom rules for this agent:</label>
                                    <textarea
                                      value={guardrailPrompt}
                                      onChange={(e) => setGuardrailPrompt(e.target.value)}
                                      className="custom-form-textarea"
                                      placeholder="e.g. Do not discuss competitors. Do not make any pricing guarantees. If asked about prices, politely tell them to contact sales."
                                      rows={4}
                                      maxLength={1000}
                                    />
                                    <div style={{ fontSize: "0.7rem", color: guardrailPrompt.length > 950 ? "#EF4444" : "#64748B", textAlign: "right", marginTop: "2px" }}>
                                      {guardrailPrompt.length} / 1000 characters
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {showGuardrailForm && (
                              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", padding: "12px", borderRadius: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div style={{ fontSize: "0.85rem", fontWeight: "bold" }}>Create Guardrail Template</div>

                                <div>
                                  <label style={{ fontSize: "0.75rem", color: "#64748B", display: "block", marginBottom: "4px" }}>Template Name</label>
                                  <input
                                    type="text"
                                    value={newGuardrailName}
                                    onChange={(e) => setNewGuardrailName(e.target.value)}
                                    className="custom-form-input"
                                    placeholder="e.g. Pricing Restrictions"
                                  />
                                </div>

                                <div>
                                  <label style={{ fontSize: "0.75rem", color: "#64748B", display: "block", marginBottom: "4px" }}>Guardrail Instructions</label>
                                  <textarea
                                    value={newGuardrailPrompt}
                                    onChange={(e) => setNewGuardrailPrompt(e.target.value)}
                                    className="custom-form-textarea"
                                    placeholder="e.g. Do not guarantee any interest rates. If asked about discounts, state that a manager will discuss it."
                                    rows={4}
                                    maxLength={1000}
                                  />
                                  <div style={{ fontSize: "0.7rem", color: newGuardrailPrompt.length > 950 ? "#EF4444" : "#64748B", textAlign: "right", marginTop: "2px" }}>
                                    {newGuardrailPrompt.length} / 1000 characters
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  disabled={isSavingGuardrail || !newGuardrailName || !newGuardrailPrompt}
                                  style={{
                                    background: primaryColor,
                                    color: "#fff",
                                    border: "none",
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "bold"
                                  }}
                                  onClick={async () => {
                                    setIsSavingGuardrail(true);
                                    try {
                                      const token = localStorage.getItem("authToken");
                                      const res = await fetch(`${baseUrl}/guardrails`, {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                          "Authorization": token ? `Bearer ${token}` : ""
                                        },
                                        body: JSON.stringify({
                                          name: newGuardrailName,
                                          prompt: newGuardrailPrompt
                                        })
                                      });
                                      if (res.ok) {
                                        const json = await res.json();
                                        if (json.success && json.data) {
                                          setSelectedGuardrailId(json.data.id);
                                          setNewGuardrailName("");
                                          setNewGuardrailPrompt("");
                                          setShowGuardrailForm(false);
                                          fetchGuardrails();
                                        }
                                      }
                                    } catch (err) {
                                      console.error(err);
                                    } finally {
                                      setIsSavingGuardrail(false);
                                    }
                                  }}
                                >
                                  {isSavingGuardrail ? "Saving..." : "Save Template"}
                                </button>
                              </div>
                            )}

                            <small style={{ color: "#64748B", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
                              These rules are synced natively with Tavus Guardrails to restrict the voice AI and prevent hallucinations.
                            </small>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Accordion 3: Look & Feel */}
                    <div className={`accordion-section ${expandedSection === "theme" ? "expanded" : ""}`}>
                      <button
                        type="button"
                        className="accordion-header"
                        onClick={() => setExpandedSection(expandedSection === "theme" ? "" : "theme")}
                      >
                        <span>🎨 Look & Feel Customizer</span>
                        <span>{expandedSection === "theme" ? "▼" : "▲"}</span>
                      </button>
                      {expandedSection === "theme" && (
                        <div className="accordion-body">
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Primary Accent</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Body Background</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={bodyBgColor} onChange={(e) => setBodyBgColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={bodyBgColor} onChange={(e) => setBodyBgColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Header Background</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={headerBgColor} onChange={(e) => setHeaderBgColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={headerBgColor} onChange={(e) => setHeaderBgColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Header Text</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={headerTextColor} onChange={(e) => setHeaderTextColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={headerTextColor} onChange={(e) => setHeaderTextColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Logo BG Color</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={logoBgColor} onChange={(e) => setLogoBgColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={logoBgColor} onChange={(e) => setLogoBgColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Input Area BG</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={inputBgColor} onChange={(e) => setInputBgColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={inputBgColor} onChange={(e) => setInputBgColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label" style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", cursor: "pointer" }}>
                                <input
                                  type="checkbox"
                                  checked={applyGreenscreen}
                                  onChange={(e) => setApplyGreenscreen(e.target.checked)}
                                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                />
                                Enable Green Screen (Chroma Key) / Transparent Avatar
                              </label>
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Avatar Background (Color/Gradient or Image/GIF URL)</label>
                              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                <input
                                  type="text"
                                  value={avatarBackground}
                                  onChange={(e) => setAvatarBackground(e.target.value)}
                                  placeholder="e.g., #090D1A, linear-gradient(#1e3c72, #2a5298), or S3 image/GIF URL"
                                  className="custom-form-input"
                                  style={{ flex: 1 }}
                                />
                                <input
                                  type="file"
                                  accept="image/*"
                                  id="avatarBgUpload"
                                  style={{ display: "none" }}
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    try {
                                      const token = localStorage.getItem("authToken");
                                      const formData = new FormData();
                                      formData.append("logo", file); // Backend expects file field named 'logo'

                                      const res = await fetch(`${baseUrl}/agents/logo/upload`, {
                                        method: "POST",
                                        headers: {
                                          Authorization: token ? `Bearer ${token}` : ""
                                        },
                                        body: formData
                                      });
                                      const json = await res.json();
                                      if (json.success && json.data && json.data.logoUrl) {
                                        setAvatarBackground(json.data.logoUrl);
                                        alert("Background image uploaded successfully!");
                                      } else {
                                        alert("Failed to upload image: " + (json.error || json.message));
                                      }
                                    } catch (err) {
                                      alert("Error uploading background: " + err.message);
                                    } finally {
                                      e.target.value = "";
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => document.getElementById("avatarBgUpload").click()}
                                  style={{
                                    background: "rgba(99, 102, 241, 0.15)",
                                    color: "#818CF8",
                                    border: "1px solid rgba(99, 102, 241, 0.3)",
                                    borderRadius: "8px",
                                    padding: "8px 14px",
                                    fontSize: "0.82rem",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap"
                                  }}
                                >
                                  📸 Upload Image/GIF
                                </button>
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Background Sizing Mode</label>
                              <select
                                value={avatarBackgroundSize}
                                onChange={(e) => setAvatarBackgroundSize(e.target.value)}
                                className="custom-form-select"
                              >
                                <option value="cover">Cover (Fill & Crop)</option>
                                <option value="contain">Contain (Fit Whole Image)</option>
                                <option value="100% 100%">Stretch to Fit</option>
                              </select>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Avatar Zoom ({Math.round(avatarZoom * 100)}%)</label>
                              <input
                                type="range"
                                min="0.5"
                                max="2.5"
                                step="0.05"
                                value={avatarZoom}
                                onChange={(e) => setAvatarZoom(Number(e.target.value))}
                                className="custom-range"
                              />
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Avatar X Offset ({avatarTranslateX}px)</label>
                              <input
                                type="range"
                                min="-150"
                                max="150"
                                step="2"
                                value={avatarTranslateX}
                                onChange={(e) => setAvatarTranslateX(Number(e.target.value))}
                                className="custom-range"
                              />
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Avatar Y Offset ({avatarTranslateY}px)</label>
                              <input
                                type="range"
                                min="-150"
                                max="150"
                                step="2"
                                value={avatarTranslateY}
                                onChange={(e) => setAvatarTranslateY(Number(e.target.value))}
                                className="custom-range"
                              />
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Idle Preview Mode</label>
                              <select
                                value={showAvatarPreview}
                                onChange={(e) => setShowAvatarPreview(e.target.value === "true" || e.target.value === true)}
                                className="custom-form-select"
                              >
                                <option value="true">Show Avatar Video Preview</option>
                                <option value="false">Show Custom Background Only</option>
                              </select>
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Minimized Pill Text</label>
                              <input type="text" value={minimizedText} onChange={(e) => setMinimizedText(e.target.value)} className="custom-form-input" />
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Pill Background</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={minimizedBgColor} onChange={(e) => setMinimizedBgColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={minimizedBgColor} onChange={(e) => setMinimizedBgColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group">
                              <label className="custom-form-label">Pill Text Color</label>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <input type="color" value={minimizedTextColor} onChange={(e) => setMinimizedTextColor(e.target.value)} style={{ width: "34px", height: "34px", border: "none", background: "none", cursor: "pointer" }} />
                                <input type="text" value={minimizedTextColor} onChange={(e) => setMinimizedTextColor(e.target.value)} className="custom-form-input" style={{ padding: "6px" }} />
                              </div>
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Composing Response Text</label>
                              <input type="text" value={composingText} onChange={(e) => setComposingText(e.target.value)} className="custom-form-input" placeholder="e.g. is composing response..." />
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Connecting Session Text</label>
                              <input type="text" value={connectingText} onChange={(e) => setConnectingText(e.target.value)} className="custom-form-input" placeholder="e.g. Launching video connection replica..." />
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Input Field Placeholder</label>
                              <input type="text" value={placeholderText} onChange={(e) => setPlaceholderText(e.target.value)} className="custom-form-input" placeholder="e.g. Type a message..." />
                            </div>
                            <div className="custom-form-group" style={{ gridColumn: "span 2" }}>
                              <label className="custom-form-label">Joining Session Text</label>
                              <input type="text" value={joiningText} onChange={(e) => setJoiningText(e.target.value)} className="custom-form-input" placeholder="e.g. Joining Voice Session..." />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Accordion 4: Layout & Position */}
                    <div className={`accordion-section ${expandedSection === "layout" ? "expanded" : ""}`}>
                      <button
                        type="button"
                        className="accordion-header"
                        onClick={() => setExpandedSection(expandedSection === "layout" ? "" : "layout")}
                      >
                        <span>📐 Layout & Positioning</span>
                        <span>{expandedSection === "layout" ? "▼" : "▲"}</span>
                      </button>
                      {expandedSection === "layout" && (
                        <div className="accordion-body">
                          <div className="custom-form-group">
                            <label className="custom-form-label">Widget Position</label>
                            <select value={position} onChange={(e) => setPosition(e.target.value)} className="custom-form-select">
                              <option value="right">Right Side</option>
                              <option value="left">Left Side</option>
                            </select>
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Vertical Align</label>
                            <select value={verticalAlign} onChange={(e) => setVerticalAlign(e.target.value)} className="custom-form-select">
                              <option value="bottom">Bottom</option>
                              <option value="top">Top</option>
                            </select>
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Video Height: {videoHeight}px</label>
                            <input type="range" min="150" max="350" step="10" value={videoHeight} onChange={(e) => setVideoHeight(Number(e.target.value))} className="custom-range" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Widget Height: {widgetHeight}px</label>
                            <input type="range" min="400" max="1000" step="10" value={widgetHeight} onChange={(e) => setWidgetHeight(Number(e.target.value))} className="custom-range" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Widget Width: {widgetWidth}px</label>
                            <input type="range" min="280" max="600" step="10" value={widgetWidth} onChange={(e) => setWidgetWidth(Number(e.target.value))} className="custom-range" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Horizontal Offset: {offsetX}px</label>
                            <input type="range" min="10" max="100" step="1" value={offsetX} onChange={(e) => setOffsetX(Number(e.target.value))} className="custom-range" />
                          </div>
                          <div className="custom-form-group">
                            <label className="custom-form-label">Vertical Offset: {offsetY}px</label>
                            <input type="range" min="10" max="100" step="1" value={offsetY} onChange={(e) => setOffsetY(Number(e.target.value))} className="custom-range" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Active Embed & Replacement Panel */}
                    <div style={{ background: "#F8FAFC", padding: "16px", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
                      <h4 style={{ color: "#1E293B", fontSize: "0.9rem", margin: "0 0 8px 0", fontWeight: "700" }}>🌐 Active Agent Replacement Control</h4>
                      <p style={{ color: "#64748B", fontSize: "0.75rem", margin: "0 0 12px 0" }}>
                        This chatbot configures the embed script. Setting this agent as primary will automatically replace the active chatbot on the homepage.
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          setIsPrimary(true);
                          alert("Agent set to Primary. Remember to click 'Save Configuration' below to apply changes to the live site.");
                        }}
                        style={{
                          width: "100%",
                          background: isPrimary ? "#10B981" : "rgba(255,255,255,0.05)",
                          color: isPrimary ? "#FFFFFF" : "#1E293B",
                          border: isPrimary ? "none" : "1px solid #E2E8F0",
                          borderRadius: "8px",
                          padding: "10px",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s"
                        }}
                      >
                        {isPrimary ? "✓ Active Primary Agent" : "Set as Active Chatbot"}
                      </button>
                    </div>

                    <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                      <button onClick={handleSaveAgent} disabled={isSavingAgent} style={{ flex: 1, background: `linear-gradient(135deg, ${primaryColor} 0%, #4F46E5 100%)`, color: "#fff", border: "none", padding: "12px", borderRadius: "10px", fontWeight: "600", cursor: "pointer", boxShadow: `0 4px 14px ${primaryColor}40` }}>{isSavingAgent ? "Saving..." : "Save Configuration"}</button>
                      {selectedAgentId !== "default" && selectedAgentId !== "new" && (
                        <button onClick={() => handleDeleteAgent(selectedAgentId)} style={{ background: "rgba(239, 68, 68, 0.15)", color: "#EF4444", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "10px", padding: "12px", cursor: "pointer", fontWeight: "600" }}>Delete</button>
                      )}
                    </div>
                    {saveStatusMsg && (
                      <div style={{ color: saveStatusMsg.startsWith("Error") ? "#EF4444" : "#10B981", fontSize: "0.8rem", textAlign: "center", marginTop: "8px" }}>
                        {saveStatusMsg}
                      </div>
                    )}
                  </div>
                )}

                {agentEditSubTab === "knowledge" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {/* Toggle Row */}
                    <div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>
                      <button
                        type="button"
                        onClick={() => setKnowledgeType("avatar")}
                        style={{
                          flex: 1,
                          padding: "10px",
                          borderRadius: "10px",
                          border: "none",
                          fontWeight: "600",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                          background: knowledgeType === "avatar" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#E2E8F0",
                          color: knowledgeType === "avatar" ? "#FFFFFF" : "#475569",
                          transition: "all 0.2s"
                        }}
                      >
                        👤 Avatar (Tavus)
                      </button>
                      <button
                        type="button"
                        onClick={() => setKnowledgeType("text")}
                        style={{
                          flex: 1,
                          padding: "10px",
                          borderRadius: "10px",
                          border: "none",
                          fontWeight: "600",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                          background: knowledgeType === "text" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#E2E8F0",
                          color: knowledgeType === "text" ? "#FFFFFF" : "#475569",
                          transition: "all 0.2s"
                        }}
                      >
                        💬 Text (Crawler)
                      </button>
                    </div>

                    <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                      <h3 style={{ color: "#1E293B", fontSize: "1rem", marginBottom: "8px" }}>Ingest Knowledge PDF ({knowledgeType === "text" ? "Text Chatbot" : "Avatar"})</h3>
                      <p style={{ color: "#64748B", fontSize: "0.85rem", marginBottom: "14px" }}>Upload product catalogs, dealership instructions, or PDFs to train your bot.</p>
                      <div style={{ border: "2px dashed #CBD5E1", borderRadius: "12px", padding: "20px", textAlign: "center", position: "relative", background: "#F8FAFC" }}>
                        <span style={{ fontSize: "1.8rem", display: "block", marginBottom: "8px" }}>📁</span>
                        <span style={{ color: "#64748B", fontSize: "0.85rem" }}>
                          Drag and drop your PDF or <label style={{ color: "#6366F1", cursor: "pointer", fontWeight: "600" }}>browse<input type="file" accept="application/pdf" onChange={handlePdfUpload} style={{ display: "none" }} disabled={uploading} /></label>
                        </span>
                        {uploading && <div style={{ marginTop: "12px", color: "#10B981", fontSize: "0.85rem", fontWeight: "600" }}>⚡ Uploading & syncing...</div>}
                        {uploadError && <div style={{ marginTop: "12px", color: "#EF4444", fontSize: "0.85rem" }}>⚠️ {uploadError}</div>}
                      </div>
                    </div>

                    <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                      <h3 style={{ color: "#1E293B", fontSize: "1rem", marginBottom: "8px" }}>Ingest Website Link / Knowledge URL ({knowledgeType === "text" ? "Text Chatbot" : "Avatar"})</h3>
                      <p style={{ color: "#64748B", fontSize: "0.8rem", marginBottom: "14px" }}>Crawl and sync live web pages for this chatbot agent.</p>
                      <form onSubmit={handleCrawlUrlSubmit} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <input
                          type="text"
                          placeholder="Document Name (e.g. Price Sheet)"
                          value={crawlNameInput}
                          onChange={(e) => setCrawlNameInput(e.target.value)}
                          className="custom-form-input"
                        />
                        <input
                          type="url"
                          placeholder="Website URL (e.g. https://example.com/info)"
                          value={crawlUrlInput}
                          onChange={(e) => setCrawlUrlInput(e.target.value)}
                          className="custom-form-input"
                          required
                        />
                        <div style={{ display: "flex", gap: "10px" }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: "0.75rem", color: "#64748B", display: "block", marginBottom: "4px", fontWeight: "600" }}>Crawl Depth (0-3)</label>
                            <input
                              type="number"
                              min="0"
                              max="3"
                              value={crawlDepth}
                              onChange={(e) => setCrawlDepth(e.target.value)}
                              className="custom-form-input"
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: "0.75rem", color: "#64748B", display: "block", marginBottom: "4px", fontWeight: "600" }}>Max Pages (1-100)</label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={crawlMaxPages}
                              onChange={(e) => setCrawlMaxPages(e.target.value)}
                              className="custom-form-input"
                            />
                          </div>
                        </div>
                        <button
                          type="submit"
                          disabled={crawling}
                          style={{
                            background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                            color: "#fff",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "8px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            alignSelf: "flex-end"
                          }}
                        >
                          {crawling ? "Crawling..." : "Crawl Link"}
                        </button>
                      </form>
                      {crawling && <div style={{ marginTop: "12px", color: "#10B981", fontSize: "0.8rem", fontWeight: "600" }}>⚡ Ingesting site content and indexing knowledge base...</div>}
                      {crawlError && <div style={{ marginTop: "12px", color: "#EF4444", fontSize: "0.8rem" }}>⚠️ {crawlError}</div>}
                    </div>

                    <h3 style={{ color: "#1E293B", fontSize: "0.95rem", margin: "10px 0 4px 0" }}>Ingested Knowledge Items ({knowledgeType === "text" ? "Text Chatbot" : "Avatar"})</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "250px", overflowY: "auto" }}>
                      {knowledgeType === "text" ? (
                        textDocs.length === 0 ? (
                          <div style={{ color: "#64748B", fontSize: "0.8rem", fontStyle: "italic" }}>No files uploaded yet.</div>
                        ) : (
                          textDocs.map((doc, idx) => (
                            <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", border: "1px solid #E2E8F0", padding: "10px 14px", borderRadius: "10px" }}>
                              <div style={{ flex: 1, minWidth: 0, marginRight: "10px" }}>
                                <div style={{ fontWeight: "600", fontSize: "0.85rem", color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>🌐 {doc.url}</div>
                                <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>Depth: {doc.max_depth || 0}</div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ color: doc.last_crawl_status === "success" ? "#10B981" : "#EF4444", fontSize: "0.75rem", fontWeight: "600" }}>{doc.last_crawl_status || "Crawled"}</span>
                                <button onClick={() => handleDeleteCrawlSite(doc.id)} style={{ background: "rgba(239, 68, 68, 0.08)", color: "#EF4444", border: "none", borderRadius: "6px", padding: "4px 8px", fontSize: "0.72rem", cursor: "pointer", fontWeight: "600" }}>Delete</button>
                              </div>
                            </div>
                          ))
                        )
                      ) : (
                        uploadedDocs.length === 0 ? (
                          <div style={{ color: "#64748B", fontSize: "0.8rem", fontStyle: "italic" }}>No files uploaded yet.</div>
                        ) : (
                          uploadedDocs.map((doc, idx) => (
                            <div key={(doc.document_id || doc.id) || idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", border: "1px solid #E2E8F0", padding: "10px 14px", borderRadius: "10px" }}>
                              <div style={{ flex: 1, minWidth: 0, marginRight: "10px" }}>
                                <div style={{ fontWeight: "600", fontSize: "0.85rem", color: "#1E293B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📄 {doc.document_name}</div>
                                <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>ID: {String(doc.document_id || doc.id).substring(0, 12)}...</div>
                              </div>
                              <button onClick={() => removeDoc(doc.document_id || doc.id)} style={{ background: "rgba(239, 68, 68, 0.08)", color: "#EF4444", border: "none", borderRadius: "6px", padding: "4px 8px", fontSize: "0.72rem", cursor: "pointer", fontWeight: "600" }}>Delete</button>
                            </div>
                          ))
                        )
                      )}
                    </div>
                  </div>
                )}



                {/* Sub Tab Panel 4: Embed SDK */}
                {agentEditSubTab === "sdk" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div style={{ background: "#FFFFFF", borderRadius: "12px", padding: "20px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                      <h3 style={{ color: "#1E293B", fontSize: "0.95rem", marginBottom: "6px" }}>Embed code for {agentName}</h3>
                      <p style={{ color: "#64748B", fontSize: "0.8rem", marginBottom: "14px" }}>Add this HTML script snippet to target website pages to render this avatar chatbot widget.</p>

                      <div className="code-box" style={{ background: "#0F172A", padding: "12px", borderRadius: "8px", color: "#38BDF8", fontSize: "0.75rem", fontFamily: "monospace", position: "relative" }}>
                        <button
                          onClick={() => {
                            const apiBase = getBackendBaseUrl();
                            navigator.clipboard.writeText(
                              `<!-- Aura AI Embed Widget -->\n<script\n  src="${apiBase}/chatbot/sdk/widget.js"\n  data-agent-id="${selectedAgentId}"\n  data-primary-color="${primaryColor}"\n  data-position="${position}"\n  data-video-height="${videoHeight}"\n  data-offset-x="${offsetX}"\n  data-offset-y="${offsetY}"\n></script>`
                            );
                            setCopiedSdk(true);
                            setTimeout(() => setCopiedSdk(false), 2000);
                          }}
                          className="copy-btn"
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            background: "rgba(255, 255, 255, 0.08)",
                            color: "#CBD5E1",
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: "4px",
                            padding: "4px 8px",
                            fontSize: "0.7rem",
                            cursor: "pointer"
                          }}
                        >
                          {copiedSdk ? "Copied! ✓" : "Copy"}
                        </button>
                        {`<!-- Aura AI Embed Widget -->
<script
  src="${getBackendBaseUrl()}/chatbot/sdk/widget.js"
  data-agent-id="${selectedAgentId}"
  data-primary-color="${primaryColor}"
  data-position="${position}"
  data-video-height="${videoHeight}"
  data-offset-x="${offsetX}"
  data-offset-y="${offsetY}"
></script>`}
                      </div>
                    </div>

                    <div style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: "12px", padding: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <h4 style={{ color: "#3730A3", margin: "0 0 2px 0", fontSize: "0.9rem" }}>🌐 Embed Live Mock Sandbox</h4>
                        <p style={{ color: "#6366F1", fontSize: "0.75rem", margin: 0 }}>Launch chatbot instantly on a mock template.</p>
                      </div>
                      <button onClick={() => setShowMockLanding(true)} style={{ background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "0.8rem" }}>Launch Sandbox</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="preview-panel-premium" style={{ padding: 0, display: "flex", flexDirection: "column" }}>
                {/* Mock Browser Body Sandbox */}
                <div ref={previewContainerCallbackRef} style={{
                  flex: 1,
                  display: "flex",
                  alignItems: previewDevice === "mobile" ? "center" : "stretch",
                  justifyContent: "center",
                  background: "#F1F5F9",
                  position: "relative",
                  overflow: "hidden",
                  padding: previewDevice === "mobile" ? "20px" : "0",
                  borderTopLeftRadius: "20px",
                  borderTopRightRadius: "20px"
                }}>
                  {previewDevice === "mobile" ? (
                    /* Mobile Frame Viewport */
                    <div className="mobile-mockup-frame" style={{ width: "330px", height: "100%", maxHeight: "560px", margin: "auto", position: "relative", borderRadius: "32px", border: "10px solid #1E293B", overflow: "hidden", boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)" }}>
                      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", background: "#FFFFFF", position: "relative" }}>
                        {previewEmbeddedMode ? (
                          <ChatWidget
                            key={`${selectedAgentId}-mobile-${primaryColor}-${agentName}-${logo}-${logoBgColor}-${bodyBgColor}-${headerBgColor}-${headerTextColor}-${inputBgColor}-${textColor}-${minimizedText}-${minimizedBgColor}-${minimizedTextColor}-${widgetHeight}-${widgetWidth}-${replicaId}-${thumbnailVideoUrl}-${voiceButtonText}-${previewEmbeddedMode}-${applyGreenscreen}-${avatarBackground}-${avatarBackgroundSize}-${avatarZoom}-${avatarTranslateX}-${avatarTranslateY}-${showAvatarPreview}-${composingText}-${connectingText}-${placeholderText}-${joiningText}`}
                            agentId={selectedAgentId}
                            primaryColor={primaryColor}
                            agentName={agentName}
                            customGreeting={customGreeting}
                            systemPrompt={systemPrompt}
                            conversationalContext={conversationalContext}
                            logo={logo}
                            position="right"
                            replicaId={replicaId}
                            embeddedMode={true}
                            documentIds={selectedAgentDocIds}
                            videoHeight={180}
                            offsetX={0}
                            offsetY={0}
                            bodyBgColor={bodyBgColor}
                            headerBgColor={headerBgColor}
                            headerTextColor={headerTextColor}
                            inputBgColor={inputBgColor}
                            textColor={textColor}
                            minimizedText={minimizedText}
                            minimizedBgColor={minimizedBgColor}
                            minimizedTextColor={minimizedTextColor}
                            widgetHeight={Math.min(widgetHeight, 540)}
                            widgetWidth={Math.min(widgetWidth, 310)}
                            verticalAlign={verticalAlign}
                            thumbnailVideoUrl={thumbnailVideoUrl}
                            voiceButtonText={voiceButtonText}
                            logoBgColor={logoBgColor}
                            previewMode={true}
                            applyGreenscreen={applyGreenscreen}
                            avatarBackground={avatarBackground}
                            avatarBackgroundSize={avatarBackgroundSize}
                            avatarZoom={avatarZoom}
                            avatarTranslateX={avatarTranslateX}
                            avatarTranslateY={avatarTranslateY}
                            showAvatarPreview={showAvatarPreview}
                            composingText={composingText}
                            connectingText={connectingText}
                            placeholderText={placeholderText}
                            joiningText={joiningText}
                          />
                        ) : (
                          <div style={{ flex: 1, position: "relative", background: "#F8FAFC" }}>
                            <ChatWidget
                              key={`${selectedAgentId}-mobile-float-${primaryColor}-${agentName}-${logo}-${logoBgColor}-${bodyBgColor}-${headerBgColor}-${headerTextColor}-${inputBgColor}-${textColor}-${minimizedText}-${minimizedBgColor}-${minimizedTextColor}-${widgetHeight}-${widgetWidth}-${replicaId}-${thumbnailVideoUrl}-${voiceButtonText}-${previewEmbeddedMode}-${applyGreenscreen}-${avatarBackground}-${avatarBackgroundSize}-${avatarZoom}-${avatarTranslateX}-${avatarTranslateY}-${showAvatarPreview}-${composingText}-${connectingText}-${placeholderText}-${joiningText}`}
                              agentId={selectedAgentId}
                              primaryColor={primaryColor}
                              agentName={agentName}
                              customGreeting={customGreeting}
                              systemPrompt={systemPrompt}
                              conversationalContext={conversationalContext}
                              logo={logo}
                              position={position}
                              replicaId={replicaId}
                              embeddedMode={false}
                              documentIds={selectedAgentDocIds}
                              videoHeight={180}
                              offsetX={10}
                              offsetY={10}
                              bodyBgColor={bodyBgColor}
                              headerBgColor={headerBgColor}
                              headerTextColor={headerTextColor}
                              inputBgColor={inputBgColor}
                              textColor={textColor}
                              minimizedText={minimizedText}
                              minimizedBgColor={minimizedBgColor}
                              minimizedTextColor={minimizedTextColor}
                              widgetHeight={Math.min(widgetHeight, 540)}
                              widgetWidth={Math.min(widgetWidth, 310)}
                              verticalAlign={verticalAlign}
                              thumbnailVideoUrl={thumbnailVideoUrl}
                              voiceButtonText={voiceButtonText}
                              logoBgColor={logoBgColor}
                              previewMode={true}
                              applyGreenscreen={applyGreenscreen}
                              avatarBackground={avatarBackground}
                              avatarBackgroundSize={avatarBackgroundSize}
                              avatarZoom={avatarZoom}
                              avatarTranslateX={avatarTranslateX}
                              avatarTranslateY={avatarTranslateY}
                              showAvatarPreview={showAvatarPreview}
                              composingText={composingText}
                              connectingText={connectingText}
                              placeholderText={placeholderText}
                              joiningText={joiningText}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Centered Chatbot Widget Viewport */
                    <div style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      background: "#FFFFFF",
                      position: "relative",
                      overflow: "hidden"
                    }}>
                      {previewEmbeddedMode ? (
                        /* Direct Centered Chatbot Widget Preview */
                        <div style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "24px",
                          background: "#F8FAFC",
                          backgroundImage: "radial-gradient(#E2E8F0 1.5px, transparent 1.5px)",
                          backgroundSize: "20px 20px"
                        }}>
                          {(() => {
                            const embedScaleX = (previewContainerSize.width - 48) / widgetWidth;
                            const embedScaleY = (previewContainerSize.height - 48) / widgetHeight;
                            const embedScale = Math.min(1, embedScaleX, embedScaleY);
                            return (
                              <div style={{
                                width: `${widgetWidth * embedScale}px`,
                                height: `${widgetHeight * embedScale}px`,
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "width 0.3s ease, height 0.3s ease"
                              }}>
                                <div style={{
                                  width: `${widgetWidth}px`,
                                  height: `${widgetHeight}px`,
                                  transform: `scale(${embedScale})`,
                                  transformOrigin: "center center",
                                  position: "absolute",
                                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
                                  borderRadius: "16px",
                                  border: "1px solid #E2E8F0",
                                  overflow: "hidden",
                                  background: "#FFFFFF",
                                  transition: "transform 0.3s ease, width 0.3s ease, height 0.3s ease"
                                }}>
                                  <ChatWidget
                                    key={`${selectedAgentId}-desktop-${primaryColor}-${agentName}-${logo}-${logoBgColor}-${bodyBgColor}-${headerBgColor}-${headerTextColor}-${inputBgColor}-${textColor}-${minimizedText}-${minimizedBgColor}-${minimizedTextColor}-${widgetHeight}-${widgetWidth}-${replicaId}-${thumbnailVideoUrl}-${voiceButtonText}-${previewEmbeddedMode}-${applyGreenscreen}-${avatarBackground}-${avatarBackgroundSize}-${avatarZoom}-${avatarTranslateX}-${avatarTranslateY}-${showAvatarPreview}-${composingText}-${connectingText}-${placeholderText}-${joiningText}`}
                                    agentId={selectedAgentId}
                                    primaryColor={primaryColor}
                                    agentName={agentName}
                                    customGreeting={customGreeting}
                                    systemPrompt={systemPrompt}
                                    conversationalContext={conversationalContext}
                                    logo={logo}
                                    position="right"
                                    replicaId={replicaId}
                                    embeddedMode={true}
                                    documentIds={selectedAgentDocIds}
                                    videoHeight={videoHeight}
                                    offsetX={0}
                                    offsetY={0}
                                    bodyBgColor={bodyBgColor}
                                    headerBgColor={headerBgColor}
                                    headerTextColor={headerTextColor}
                                    inputBgColor={inputBgColor}
                                    textColor={textColor}
                                    minimizedText={minimizedText}
                                    minimizedBgColor={minimizedBgColor}
                                    minimizedTextColor={minimizedTextColor}
                                    widgetHeight={widgetHeight}
                                    widgetWidth={widgetWidth}
                                    verticalAlign={verticalAlign}
                                    thumbnailVideoUrl={thumbnailVideoUrl}
                                    voiceButtonText={voiceButtonText}
                                    logoBgColor={logoBgColor}
                                    previewMode={true}
                                    applyGreenscreen={applyGreenscreen}
                                    avatarBackground={avatarBackground}
                                    avatarBackgroundSize={avatarBackgroundSize}
                                    avatarZoom={avatarZoom}
                                    avatarTranslateX={avatarTranslateX}
                                    avatarTranslateY={avatarTranslateY}
                                    showAvatarPreview={showAvatarPreview}
                                    composingText={composingText}
                                    connectingText={connectingText}
                                    placeholderText={placeholderText}
                                    joiningText={joiningText}
                                  />
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        /* Realistic Float Sandbox Preview (Bubble Mode) */
                        <div style={{
                          flex: 1,
                          position: "relative",
                          background: "#F8FAFC",
                          backgroundImage: "radial-gradient(#E2E8F0 1.5px, transparent 1.5px)",
                          backgroundSize: "20px 20px",
                          overflow: "hidden"
                        }}>
                          {(() => {
                            const floatScaleX = previewContainerSize.width / (widgetWidth + offsetX + 20);
                            const floatScaleY = previewContainerSize.height / (widgetHeight + offsetY + 20);
                            const floatScale = Math.min(1, floatScaleX, floatScaleY);
                            return (
                              <div style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                pointerEvents: "none"
                              }}>
                                <div style={{
                                  position: "absolute",
                                  width: "100%",
                                  height: "100%",
                                  pointerEvents: "none",
                                  transform: `scale(${floatScale})`,
                                  transformOrigin: `${verticalAlign || "bottom"} ${position || "right"}`,
                                  transition: "transform 0.3s ease"
                                }}>
                                  {/* Floating ChatWidget precisely positioned inside the canvas */}
                                  <ChatWidget
                                    key={`${selectedAgentId}-desktop-float-${primaryColor}-${agentName}-${logo}-${logoBgColor}-${bodyBgColor}-${headerBgColor}-${headerTextColor}-${inputBgColor}-${textColor}-${minimizedText}-${minimizedBgColor}-${minimizedTextColor}-${widgetHeight}-${widgetWidth}-${replicaId}-${thumbnailVideoUrl}-${voiceButtonText}-${previewEmbeddedMode}-${applyGreenscreen}-${avatarBackground}-${avatarBackgroundSize}-${avatarZoom}-${avatarTranslateX}-${avatarTranslateY}-${showAvatarPreview}-${composingText}-${connectingText}-${placeholderText}-${joiningText}`}
                                    agentId={selectedAgentId}
                                    primaryColor={primaryColor}
                                    agentName={agentName}
                                    customGreeting={customGreeting}
                                    systemPrompt={systemPrompt}
                                    conversationalContext={conversationalContext}
                                    logo={logo}
                                    position={position}
                                    replicaId={replicaId}
                                    embeddedMode={false}
                                    documentIds={selectedAgentDocIds}
                                    videoHeight={videoHeight}
                                    offsetX={offsetX}
                                    offsetY={offsetY}
                                    bodyBgColor={bodyBgColor}
                                    headerBgColor={headerBgColor}
                                    headerTextColor={headerTextColor}
                                    inputBgColor={inputBgColor}
                                    textColor={textColor}
                                    minimizedText={minimizedText}
                                    minimizedBgColor={minimizedBgColor}
                                    minimizedTextColor={minimizedTextColor}
                                    widgetHeight={widgetHeight}
                                    widgetWidth={widgetWidth}
                                    verticalAlign={verticalAlign}
                                    thumbnailVideoUrl={thumbnailVideoUrl}
                                    voiceButtonText={voiceButtonText}
                                    logoBgColor={logoBgColor}
                                    previewMode={true}
                                    applyGreenscreen={applyGreenscreen}
                                    avatarBackground={avatarBackground}
                                    avatarBackgroundSize={avatarBackgroundSize}
                                    avatarZoom={avatarZoom}
                                    avatarTranslateX={avatarTranslateX}
                                    avatarTranslateY={avatarTranslateY}
                                    showAvatarPreview={showAvatarPreview}
                                    composingText={composingText}
                                    connectingText={connectingText}
                                    placeholderText={placeholderText}
                                    joiningText={joiningText}
                                  />
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Floating Preview Control Bar */}
                <div style={{
                  background: "#F8FAFC",
                  borderTop: "1.5px solid #E2E8F0",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "0.78rem",
                  color: "#64748B",
                  borderBottomLeftRadius: "20px",
                  borderBottomRightRadius: "20px",
                  flexWrap: "wrap",
                  gap: "12px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>Device:</span>
                    <button
                      type="button"
                      onClick={() => setPreviewDevice("desktop")}
                      style={{
                        background: previewDevice === "desktop" ? "#6366F1" : "#E2E8F0",
                        color: previewDevice === "desktop" ? "#FFFFFF" : "#475569",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 10px",
                        fontSize: "0.75rem",
                        cursor: "pointer",
                        fontWeight: "700"
                      }}
                    >
                      💻 Desktop
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewDevice("mobile")}
                      style={{
                        background: previewDevice === "mobile" ? "#6366F1" : "#E2E8F0",
                        color: previewDevice === "mobile" ? "#FFFFFF" : "#475569",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 10px",
                        fontSize: "0.75rem",
                        cursor: "pointer",
                        fontWeight: "700"
                      }}
                    >
                      📱 Mobile
                    </button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>Preview State:</span>
                    <button
                      type="button"
                      onClick={() => setPreviewEmbeddedMode(!previewEmbeddedMode)}
                      style={{
                        background: "#4F46E5",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "0.75rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s"
                      }}
                    >
                      {previewEmbeddedMode ? "Switch to Floating Bubble Mode" : "Switch to Expanded View"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        )}

        {/* KNOWLEDGE TAB */}
        {activeTab === "knowledge" && (
          <div>
            {/* Toggle Row */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
              <button
                type="button"
                onClick={() => setKnowledgeType("avatar")}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "12px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  background: knowledgeType === "avatar" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#E2E8F0",
                  color: knowledgeType === "avatar" ? "#FFFFFF" : "#475569",
                  transition: "all 0.2s",
                  boxShadow: knowledgeType === "avatar" ? "0 4px 12px rgba(99, 102, 241, 0.25)" : "none"
                }}
              >
                👤 Avatar Knowledge (Tavus Sync)
              </button>
              <button
                type="button"
                onClick={() => setKnowledgeType("text")}
                style={{
                  flex: 1,
                  padding: "12px",
                  borderRadius: "12px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  background: knowledgeType === "text" ? "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" : "#E2E8F0",
                  color: knowledgeType === "text" ? "#FFFFFF" : "#475569",
                  transition: "all 0.2s",
                  boxShadow: knowledgeType === "text" ? "0 4px 12px rgba(99, 102, 241, 0.25)" : "none"
                }}
              >
                💬 Text Chatbot Knowledge (Crawler)
              </button>
            </div>

            <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "16px", padding: "32px", marginBottom: "32px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <h3 style={{ color: "#1E293B", marginBottom: "8px" }}>Ingest Knowledge PDF ({knowledgeType === "text" ? "Text Chatbot" : "Avatar"})</h3>
              <p style={{ color: "#64748B", fontSize: "0.85rem", marginBottom: "20px" }}>Upload product catalogs, dealership instructions, or PDFs to train your bot.</p>
              <div style={{ border: "2px dashed #CBD5E1", borderRadius: "12px", padding: "30px", textAlign: "center", position: "relative", background: "#F8FAFC" }}>
                <span style={{ fontSize: "2rem", display: "block", marginBottom: "12px" }}>📁</span>
                <span style={{ color: "#64748B", fontSize: "0.9rem" }}>Drag and drop your PDF here or <label style={{ color: "#6366F1", cursor: "pointer", fontWeight: "600" }}>browse<input type="file" accept="application/pdf" onChange={handlePdfUpload} style={{ display: "none" }} disabled={uploading} /></label></span>
                {uploading && <div style={{ marginTop: "16px", color: "#10B981", fontSize: "0.85rem", fontWeight: "600" }}>⚡ Uploading and syncing...</div>}
                {uploadError && <div style={{ marginTop: "16px", color: "#EF4444", fontSize: "0.85rem" }}>⚠️ {uploadError}</div>}
              </div>
            </div>

            <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "16px", padding: "32px", marginBottom: "32px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <h3 style={{ color: "#1E293B", marginBottom: "8px" }}>Ingest Website Link / Knowledge URL ({knowledgeType === "text" ? "Text Chatbot" : "Avatar"})</h3>
              <p style={{ color: "#64748B", fontSize: "0.85rem", marginBottom: "20px" }}>Train your bot using live web content (e.g., dealership inventories, landing pages).</p>
              <form onSubmit={handleCrawlUrlSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    placeholder="Document Name (e.g., Phil Long New Inventory)"
                    value={crawlNameInput}
                    onChange={(e) => setCrawlNameInput(e.target.value)}
                    className="custom-form-input"
                    style={{ flex: 1, minWidth: "200px" }}
                  />
                  <input
                    type="url"
                    placeholder="Website URL (e.g., https://www.phillong.com/new-inventory/index.htm)"
                    value={crawlUrlInput}
                    onChange={(e) => setCrawlUrlInput(e.target.value)}
                    className="custom-form-input"
                    style={{ flex: 2, minWidth: "300px" }}
                    required
                  />
                </div>
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "120px" }}>
                    <label style={{ fontSize: "0.8rem", color: "#64748B", display: "block", marginBottom: "6px", fontWeight: "600" }}>Crawl Depth (0 - 3)</label>
                    <input
                      type="number"
                      min="0"
                      max="3"
                      value={crawlDepth}
                      onChange={(e) => setCrawlDepth(e.target.value)}
                      className="custom-form-input"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: "120px" }}>
                    <label style={{ fontSize: "0.8rem", color: "#64748B", display: "block", marginBottom: "6px", fontWeight: "600" }}>Max Pages to Crawl (1 - 100)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={crawlMaxPages}
                      onChange={(e) => setCrawlMaxPages(e.target.value)}
                      className="custom-form-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={crawling}
                  style={{
                    background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    alignSelf: "flex-end"
                  }}
                >
                  {crawling ? "Initiating Crawl..." : "Ingest & Crawl Link"}
                </button>
              </form>
              {crawling && <div style={{ marginTop: "16px", color: "#10B981", fontSize: "0.85rem", fontWeight: "600" }}>⚡ Ingesting site content and indexing knowledge base...</div>}
              {crawlError && <div style={{ marginTop: "16px", color: "#EF4444", fontSize: "0.85rem" }}>⚠️ {crawlError}</div>}
            </div>

            <h3 style={{ color: "#1E293B", marginBottom: "16px" }}>Ingested Knowledge Items ({knowledgeType === "text" ? "Text Chatbot" : "Avatar"})</h3>
            <div className="knowledge-grid">
              {knowledgeType === "text" ? (
                textDocs.length === 0 ? (
                  <div style={{ color: "#64748B", fontSize: "0.9rem", fontStyle: "italic" }}>No files uploaded yet.</div>
                ) : (
                   textDocs.map((doc, idx) => (
                    <div key={idx} className="knowledge-card">
                      <div className="knowledge-title">🌐 {doc.url || "Knowledge Site"}</div>
                      <div className="knowledge-meta">
                        <span>Depth: {doc.max_depth || 0}</span>
                        <span style={{ color: doc.last_crawl_status === "success" ? "#10B981" : "#EF4444", fontWeight: "600" }}>
                          {doc.last_crawl_status || "Crawled"}
                        </span>
                      </div>
                      <button onClick={() => handleDeleteCrawlSite(doc.id)} style={{ marginTop: "8px", background: "rgba(239, 68, 68, 0.1)", color: "#EF4444", border: "none", borderRadius: "8px", padding: "6px", fontSize: "0.75rem", cursor: "pointer", width: "100%", fontWeight: "600" }}>Delete Crawled Site</button>
                    </div>
                  ))
                )
              ) : (
                uploadedDocs.length === 0 ? (
                  <div style={{ color: "#64748B", fontSize: "0.9rem", fontStyle: "italic" }}>No files uploaded yet.</div>
                ) : (
                  uploadedDocs.map((doc, idx) => (
                    <div key={(doc.document_id || doc.id) || idx} className="knowledge-card">
                      <div className="knowledge-title">📄 {doc.document_name || "Knowledge Item"}</div>
                      <div className="knowledge-meta">
                        <span>ID: {String(doc.document_id || doc.id).substring(0, 10)}...</span>
                        <span style={{ color: "#10B981", fontWeight: "600" }}>Ingested</span>
                      </div>
                      <button onClick={() => removeDoc(doc.document_id || doc.id)} style={{ marginTop: "8px", background: "rgba(239, 68, 68, 0.1)", color: "#EF4444", border: "none", borderRadius: "8px", padding: "6px", fontSize: "0.75rem", cursor: "pointer" }}>Delete Ingested File</button>
                    </div>
                  ))
                )
              )}
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === "analytics" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%", height: "100%", overflowY: "auto", paddingRight: "10px", paddingTop: "20px", animation: "fadeIn 0.2s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <h2 style={{ fontSize: "1.4rem", fontWeight: "800", color: "#1E293B", margin: 0 }}>Chatbot Overview & Leads</h2>
            </div>
            <div className="stats-grid">
              <div className="stats-card">
                <div className="stats-label">Total Chats</div>
                <div className="stats-value">8,642</div>
              </div>
              <div className="stats-card">
                <div className="stats-label">Avg Session Duration</div>
                <div className="stats-value">2m 44s</div>
              </div>
              <div className="stats-card">
                <div className="stats-label">Total Call Minutes</div>
                <div className="stats-value">4,120 min</div>
              </div>
              <div className="stats-card">
                <div className="stats-label">Qualified Leads</div>
                <div className="stats-value">{leadsList.length}</div>
              </div>
            </div>

            <div style={{ marginTop: "32px", background: "#FFFFFF", borderRadius: "16px", padding: "24px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
                <h3 style={{ fontSize: "1.1rem", color: "#1E293B", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>📋 Captured Inquiries & Leads</span>
                  {fetchingLeads && <span style={{ fontSize: "0.8rem", color: "#6366F1", fontWeight: "normal" }}>Syncing...</span>}
                </h3>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <label htmlFor="agentFilterSelect" style={{ fontSize: "0.85rem", fontWeight: "600", color: "#94A3B8" }}>Filter by Chatbot:</label>
                  <select
                    id="agentFilterSelect"
                    value={leadAgentFilter}
                    onChange={(e) => setLeadAgentFilter(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      background: "rgba(8, 12, 22, 0.8)",
                      fontSize: "0.85rem",
                      color: "#F1F5F9",
                      outline: "none",
                      cursor: "pointer"
                    }}
                  >
                    <option value="all">All Chatbots ({leadsList.length})</option>
                    <option value="default">Ava (Default) ({leadsList.filter(l => l.agentId === "default" || !l.agentId).length})</option>
                    {agentsList.filter(a => a.id !== "default").map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({leadsList.filter(l => l.agentId === a.id).length})</option>
                    ))}
                  </select>
                </div>
              </div>

              {leadsList.length === 0 ? (
                <div style={{ color: "#64748B", fontSize: "0.9rem", fontStyle: "italic", textAlign: "center", padding: "24px" }}>
                  No leads captured yet. Submit callback requests inside the chat widget to start collecting inquiries.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.85rem" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #E2E8F0", color: "#64748B" }}>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Name</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Email</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Phone</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Reason / Inquiry</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Agent</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsList
                        .filter(lead => leadAgentFilter === "all" || lead.agentId === leadAgentFilter || (leadAgentFilter === "default" && !lead.agentId))
                        .map((lead) => (
                          <tr key={lead._id || lead.id} style={{ borderBottom: "1px solid #F1F5F9", color: "#1E293B" }}>
                            <td style={{ padding: "12px 8px", fontWeight: "600" }}>{lead.name}</td>
                            <td style={{ padding: "12px 8px", color: "#6366F1" }}>{lead.email}</td>
                            <td style={{ padding: "12px 8px", color: "#475569" }}>{lead.phone}</td>
                            <td style={{ padding: "12px 8px", color: "#64748B", maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.reason}</td>
                            <td style={{ padding: "12px 8px" }}><span style={{ background: "#EEF2FF", color: "#6366F1", padding: "2px 8px", borderRadius: "8px", fontSize: "0.75rem", fontWeight: "600" }}>{getAgentNameById(lead.agentId)}</span></td>
                            <td style={{ padding: "12px 8px", color: "#94A3B8" }}>{new Date(lead.createdAt).toLocaleDateString()}</td>
                            <td style={{ padding: "12px 8px" }}>
                              <button
                                onClick={() => setSelectedLeadForModal(lead)}
                                style={{
                                  background: "#6366F1",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "4px 10px",
                                  fontSize: "0.75rem",
                                  cursor: "pointer",
                                  fontWeight: "600"
                                }}
                              >
                                🔍 View
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STYLE CUSTOMIZER TAB */}
        {activeTab === "settings" && (
          <div className="customizer-container-premium">
            <div className="editor-panel-premium">
              <h3 style={{ color: "#1E293B", fontSize: "1.1rem", margin: "0 0 10px 0" }}>Visual Theme Customizer</h3>

              <div className="custom-form-group">
                <label className="custom-form-label">Primary Theme Accent</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Body Background Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={bodyBgColor} onChange={(e) => setBodyBgColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={bodyBgColor} onChange={(e) => setBodyBgColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Header Background Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={headerBgColor} onChange={(e) => setHeaderBgColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={headerBgColor} onChange={(e) => setHeaderBgColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Header Text Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={headerTextColor} onChange={(e) => setHeaderTextColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={headerTextColor} onChange={(e) => setHeaderTextColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Text Input Background Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={inputBgColor} onChange={(e) => setInputBgColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={inputBgColor} onChange={(e) => setInputBgColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Primary Text Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Logo Emoji</label>
                <input type="text" value={logo} onChange={(e) => setLogo(e.target.value)} className="custom-form-input" />
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Logo Background Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={logoBgColor} onChange={(e) => setLogoBgColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={logoBgColor} onChange={(e) => setLogoBgColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Voice Button Text</label>
                <input type="text" value={voiceButtonText} onChange={(e) => setVoiceButtonText(e.target.value)} className="custom-form-input" />
              </div>

              <h4 style={{ color: "#1E293B", fontSize: "0.9rem", margin: "16px 0 8px 0", borderTop: "1px solid #E2E8F0", paddingTop: "12px", fontWeight: "700" }}>Minimized State Customization</h4>
              <div className="custom-form-group">
                <label className="custom-form-label">Minimized Pill Text</label>
                <input type="text" value={minimizedText} onChange={(e) => setMinimizedText(e.target.value)} className="custom-form-input" />
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Minimized Pill Background</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={minimizedBgColor} onChange={(e) => setMinimizedBgColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={minimizedBgColor} onChange={(e) => setMinimizedBgColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Minimized Pill Text Color</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input type="color" value={minimizedTextColor} onChange={(e) => setMinimizedTextColor(e.target.value)} style={{ width: "36px", height: "36px", border: "none", background: "none", cursor: "pointer" }} />
                  <input type="text" value={minimizedTextColor} onChange={(e) => setMinimizedTextColor(e.target.value)} className="custom-form-input" />
                </div>
              </div>
              <h4 style={{ color: "#1E293B", fontSize: "0.9rem", margin: "16px 0 8px 0", borderTop: "1px solid #E2E8F0", paddingTop: "12px", fontWeight: "700" }}>Text Customization</h4>
              <div className="custom-form-group">
                <label className="custom-form-label">Composing Response Text</label>
                <input type="text" value={composingText} onChange={(e) => setComposingText(e.target.value)} className="custom-form-input" placeholder="e.g. is composing response..." />
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Connecting Session Text</label>
                <input type="text" value={connectingText} onChange={(e) => setConnectingText(e.target.value)} className="custom-form-input" placeholder="e.g. Launching video connection replica..." />
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Input Field Placeholder</label>
                <input type="text" value={placeholderText} onChange={(e) => setPlaceholderText(e.target.value)} className="custom-form-input" placeholder="e.g. Type a message..." />
              </div>
              <div className="custom-form-group">
                <label className="custom-form-label">Joining Session Text</label>
                <input type="text" value={joiningText} onChange={(e) => setJoiningText(e.target.value)} className="custom-form-input" placeholder="e.g. Joining Voice Session..." />
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
                <button onClick={handleSaveAgent} disabled={isSavingAgent} style={{ flex: 1, background: `linear-gradient(135deg, ${primaryColor} 0%, #4F46E5 100%)`, color: "#fff", border: "none", padding: "12px", borderRadius: "10px", fontWeight: "600", cursor: "pointer", boxShadow: `0 4px 14px ${primaryColor}40` }}>{isSavingAgent ? "Saving..." : "Save Theme Settings"}</button>
              </div>
              {saveStatusMsg && <div style={{ color: saveStatusMsg.startsWith("Error") ? "#EF4444" : "#10B981", fontSize: "0.8rem", textAlign: "center", marginTop: "8px" }}>{saveStatusMsg}</div>}
            </div>

            <div className="preview-panel-premium">
              <div className="preview-controls">
                <div className="preview-controls-title">Live Theme Preview</div>
                <div className="switcher-group">
                  <button
                    className={`switcher-btn ${previewDevice === "desktop" ? "active" : ""}`}
                    onClick={() => setPreviewDevice("desktop")}
                    title="Desktop Preview"
                  >
                    💻 Desktop
                  </button>
                  <button
                    className={`switcher-btn ${previewDevice === "mobile" ? "active" : ""}`}
                    onClick={() => setPreviewDevice("mobile")}
                    title="Mobile Preview"
                  >
                    📱 Mobile
                  </button>
                </div>
              </div>

              <div className="sandbox-viewport">
                <div className={previewDevice === "mobile" ? "mobile-mockup-frame" : "desktop-mockup-frame"}>
                  <ChatWidget
                    key={`${selectedAgentId}-${primaryColor}-${agentName}-${logo}-${logoBgColor}-${bodyBgColor}-${headerBgColor}-${headerTextColor}-${inputBgColor}-${textColor}-${minimizedText}-${minimizedBgColor}-${minimizedTextColor}-${widgetHeight}-${widgetWidth}-${replicaId}-${thumbnailVideoUrl}-${voiceButtonText}-${previewEmbeddedMode}-${applyGreenscreen}-${avatarBackground}-${avatarBackgroundSize}-${avatarZoom}-${avatarTranslateX}-${avatarTranslateY}-${showAvatarPreview}-${composingText}-${connectingText}-${placeholderText}-${joiningText}`}
                    agentId={selectedAgentId}
                    primaryColor={primaryColor}
                    agentName={agentName}
                    customGreeting={customGreeting}
                    systemPrompt={systemPrompt}
                    conversationalContext={conversationalContext}
                    logo={logo}
                    position={position}
                    replicaId={replicaId}
                    embeddedMode={previewEmbeddedMode}
                    documentIds={selectedAgentDocIds}
                    videoHeight={previewDevice === "mobile" ? 180 : videoHeight}
                    offsetX={offsetX}
                    offsetY={offsetY}
                    bodyBgColor={bodyBgColor}
                    headerBgColor={headerBgColor}
                    headerTextColor={headerTextColor}
                    inputBgColor={inputBgColor}
                    textColor={textColor}
                    minimizedText={minimizedText}
                    minimizedBgColor={minimizedBgColor}
                    minimizedTextColor={minimizedTextColor}
                    widgetHeight={previewDevice === "mobile" ? 560 : widgetHeight}
                    widgetWidth={previewDevice === "mobile" ? 330 : widgetWidth}
                    verticalAlign={verticalAlign}
                    thumbnailVideoUrl={thumbnailVideoUrl}
                    voiceButtonText={voiceButtonText}
                    logoBgColor={logoBgColor}
                    applyGreenscreen={applyGreenscreen}
                    avatarBackground={avatarBackground}
                    avatarBackgroundSize={avatarBackgroundSize}
                    avatarZoom={avatarZoom}
                    avatarTranslateX={avatarTranslateX}
                    avatarTranslateY={avatarTranslateY}
                    showAvatarPreview={showAvatarPreview}
                    composingText={composingText}
                    connectingText={connectingText}
                    placeholderText={placeholderText}
                    joiningText={joiningText}
                  />
                </div>
              </div>

              <div className="floating-preview-overlay">
                <span style={{ color: "#94A3B8" }}>Testing chatbot bubble state:</span>
                <button
                  type="button"
                  className="btn-pill-action"
                  onClick={() => setPreviewEmbeddedMode(!previewEmbeddedMode)}
                >
                  {previewEmbeddedMode ? "Switch to Floating Bubble Mode" : "Switch to Expanded View"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* WIDGET SDK TAB */}
        {activeTab === "sdk" && (
          <div style={{ background: "#FFFFFF", borderRadius: "16px", padding: "32px", border: "1px solid #E2E8F0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <h3 style={{ color: "#1E293B", marginBottom: "8px" }}>Website Embedding Code</h3>
            <p style={{ color: "#64748B", fontSize: "0.85rem", marginBottom: "20px" }}>Add this snippet to the HTML body block of any site to render the widget.</p>

            <div className="code-box">
              <button
                onClick={() => {
                  const apiBase = getBackendBaseUrl();
                  navigator.clipboard.writeText(
                    `<!-- Aura AI Embed Widget -->\n<script\n  src="${apiBase}/chatbot/sdk/widget.js"\n  data-agent-id="${selectedAgentId}"\n  data-primary-color="${primaryColor}"\n  data-position="${position}"\n  data-video-height="${videoHeight}"\n  data-offset-x="${offsetX}"\n  data-offset-y="${offsetY}"\n></script>`
                  );
                  setCopiedSdk(true);
                  setTimeout(() => setCopiedSdk(false), 2000);
                }}
                className="copy-btn"
              >
                {copiedSdk ? "Copied! ✓" : "Copy Code"}
              </button>
              {`<!-- Aura AI Embed Widget -->
<script
  src="${getBackendBaseUrl()}/chatbot/sdk/widget.js"
  data-agent-id="${selectedAgentId}"
  data-primary-color="${primaryColor}"
  data-position="${position}"
  data-video-height="${videoHeight}"
  data-offset-x="${offsetX}"
  data-offset-y="${offsetY}"
></script>`}
            </div>

            {/* Load Chatbot from pasted Script */}
            <div style={{ marginTop: "32px", padding: "24px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px" }}>
              <h4 style={{ color: "#1E293B", marginBottom: "6px" }}>🔌 Load Chatbot from SDK Script</h4>
              <p style={{ color: "#64748B", fontSize: "0.8rem", marginBottom: "16px" }}>Paste any chatbot's SDK embed script below to load and modify its visual configuration.</p>

              <textarea
                rows={3}
                placeholder={`e.g., <script src="https://rc3t9mm4e4.execute-api.ap-south-1.amazonaws.com/dev/chatbot/sdk/widget.js" data-agent-id="agent-xyz"></script>`}
                value={pastedSdkScript}
                onChange={(e) => setPastedSdkScript(e.target.value)}
                style={{
                  width: "100%",
                  background: "#F8FAFC",
                  color: "#1E293B",
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: "8px",
                  padding: "12px",
                  outline: "none",
                  resize: "vertical"
                }}
              />

              <button
                onClick={handleLoadFromScript}
                style={{
                  marginTop: "12px",
                  background: "linear-gradient(135deg, #38BDF8 0%, #0284C7 100%)",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "0.82rem",
                  fontWeight: "600",
                  cursor: "pointer"
                }}
              >
                Load Chatbot Configuration
              </button>

              {loadScriptError && <div style={{ color: "#F87171", fontSize: "0.78rem", marginTop: "8px" }}>⚠️ {loadScriptError}</div>}
              {loadScriptSuccess && <div style={{ color: "#4ADE80", fontSize: "0.78rem", marginTop: "8px" }}>✓ {loadScriptSuccess}</div>}
            </div>

            <div style={{ marginTop: "32px", background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: "12px", padding: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h4 style={{ color: "#3730A3", marginBottom: "4px" }}>🌐 Embed Live Mock Sandbox</h4>
                <p style={{ color: "#6366F1", fontSize: "0.8rem" }}>Launch chatbot instantly on a mock template.</p>
              </div>
              <button onClick={() => setShowMockLanding(true)} style={{ background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "8px", fontWeight: "600", cursor: "pointer" }}>Launch Sandbox</button>
            </div>
          </div>
        )}
      </div>

      {/* Mock Sandbox Overlay */}
      {showMockLanding && (
        <div style={{ position: "fixed", inset: 0, background: "#0A0E1A", zIndex: 99999, display: "flex", flexDirection: "column", color: "#fff", overflow: "auto" }}>
          <div style={{ padding: "16px 40px", background: "#111827", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}><span style={{ fontSize: "1.5rem" }}>🐝</span><span style={{ fontWeight: 800, fontSize: "1.2rem" }}>beeline</span></div>
            <button onClick={() => setShowMockLanding(false)} style={{ background: "rgba(239, 68, 68, 0.1)", color: "#EF4444", border: "1px solid rgba(239, 68, 68, 0.3)", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontWeight: "600" }}>Exit Demo ✕</button>
          </div>
          <div style={{ padding: "120px 40px 100px", textAlign: "center", maxWidth: "900px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "24px" }}>
            <h1 style={{ fontSize: "3.2rem", fontWeight: "800", lineHeight: "1.1", background: "linear-gradient(135deg, #fff 0%, #94A3B8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Workforce Contingent Solutions</h1>
            <p style={{ color: "#94A3B8", fontSize: "1.1rem", maxWidth: "680px" }}>Manage statement of work and independent contractors, backed by Aura AI assistant.</p>
            <ChatWidget key={`${selectedAgentId}-${logoBgColor}-${primaryColor}-${applyGreenscreen}-${avatarBackground}-${avatarBackgroundSize}-${avatarZoom}-${avatarTranslateX}-${avatarTranslateY}-${showAvatarPreview}-${composingText}-${connectingText}-${placeholderText}-${joiningText}`} agentId={selectedAgentId} primaryColor={primaryColor} agentName={agentName} customGreeting={customGreeting} systemPrompt={systemPrompt} conversationalContext={conversationalContext} logo={logo} position={position} replicaId={replicaId} embeddedMode={false} documentIds={selectedAgentDocIds} videoHeight={videoHeight} offsetX={offsetX} offsetY={offsetY} bodyBgColor={bodyBgColor} headerBgColor={headerBgColor} headerTextColor={headerTextColor} inputBgColor={inputBgColor} textColor={textColor} minimizedText={minimizedText} minimizedBgColor={minimizedBgColor} minimizedTextColor={minimizedTextColor} widgetHeight={widgetHeight} widgetWidth={widgetWidth} verticalAlign={verticalAlign} logoBgColor={logoBgColor} applyGreenscreen={applyGreenscreen} avatarBackground={avatarBackground} avatarBackgroundSize={avatarBackgroundSize} avatarZoom={avatarZoom} avatarTranslateX={avatarTranslateX} avatarTranslateY={avatarTranslateY} showAvatarPreview={showAvatarPreview} composingText={composingText} connectingText={connectingText} placeholderText={placeholderText} joiningText={joiningText} />
          </div>
        </div>
      )}
      {/* Lead Details Modal */}
      {selectedLeadForModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.4)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999999
        }}>
          <div style={{
            background: "rgba(13,20,38,0.8)",
            borderRadius: "16px",
            padding: "28px",
            width: "500px",
            maxWidth: "95%",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            animation: "fadeIn 0.2s ease",
            fontFamily: "'Inter', sans-serif"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid rgba(255, 255, 255, 0.08)", paddingBottom: "12px" }}>
              <h3 style={{ margin: 0, color: "#FFFFFF", fontSize: "1.15rem", fontWeight: "700" }}>📋 Lead Details & Inquiry</h3>
              <button onClick={() => setSelectedLeadForModal(null)} style={{ background: "none", border: "none", fontSize: "1.2rem", color: "#94A3B8", cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <span style={{ fontSize: "0.75rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Visitor Name</span>
                <div style={{ fontSize: "0.95rem", color: "#FFFFFF", fontWeight: "600", marginTop: "4px" }}>{selectedLeadForModal.name}</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <span style={{ fontSize: "0.75rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Email Address</span>
                  <div style={{ fontSize: "0.9rem", color: "#38BDF8", fontWeight: "500", marginTop: "4px" }}>{selectedLeadForModal.email}</div>
                </div>
                <div>
                  <span style={{ fontSize: "0.75rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Phone Number</span>
                  <div style={{ fontSize: "0.9rem", color: "#FFFFFF", fontWeight: "500", marginTop: "4px" }}>{selectedLeadForModal.phone}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <span style={{ fontSize: "0.75rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Assigned Agent</span>
                  <div style={{ marginTop: "4px" }}>
                    <span style={{ background: "rgba(99, 102, 241, 0.1)", color: "#818CF8", padding: "4px 10px", borderRadius: "8px", fontSize: "0.75rem", fontWeight: "600" }}>
                      {selectedLeadForModal.agentId}
                    </span>
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: "0.75rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Date Captured</span>
                  <div style={{ fontSize: "0.9rem", color: "#FFFFFF", fontWeight: "500", marginTop: "4px" }}>
                    {new Date(selectedLeadForModal.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>

              <div style={{ background: "rgba(8, 12, 22, 0.6)", padding: "14px", borderRadius: "10px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
                <span style={{ fontSize: "0.75rem", color: "#94A3B8", textTransform: "uppercase", fontWeight: "700" }}>Conversation Inquiry / Reason</span>
                <div style={{ fontSize: "0.85rem", color: "#CBD5E1", marginTop: "6px", lineHeight: "1.5", whiteSpace: "pre-line" }}>
                  {selectedLeadForModal.reason}
                </div>
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setSelectedLeadForModal(null)}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#FFFFFF",
                  borderRadius: "8px",
                  padding: "8px 20px",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background 0.2s"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Agent-specific Leads Modal */}
      {activeLeadModalAgent && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.4)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 99999
        }}>
          <div style={{
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "28px",
            width: "800px",
            maxWidth: "95%",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            border: "1px solid #E2E8F0",
            animation: "fadeIn 0.2s ease",
            fontFamily: "'Inter', sans-serif"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid #E2E8F0", paddingBottom: "12px" }}>
              <div>
                <h3 style={{ margin: 0, color: "#1E293B", fontSize: "1.2rem", fontWeight: "700" }}>
                  📋 Leads Captured by: {activeLeadModalAgent.name}
                </h3>
                <span style={{ fontSize: "0.75rem", color: "#64748B" }}>Agent ID: {activeLeadModalAgent.id}</span>
              </div>
              <button onClick={() => { setActiveLeadModalAgent(null); setModalLeadsSearch(""); }} style={{ background: "none", border: "none", fontSize: "1.2rem", color: "#64748B", cursor: "pointer" }}>✕</button>
            </div>

            {/* Modal Controls */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
              <input
                type="text"
                placeholder="Search leads by name, email, phone or inquiry..."
                value={modalLeadsSearch}
                onChange={(e) => setModalLeadsSearch(e.target.value)}
                className="custom-form-input"
                style={{ width: "300px", margin: 0 }}
              />

              <button
                onClick={() => handleExportCsv(activeLeadModalAgent.id, activeLeadModalAgent.name)}
                disabled={leadsList.filter(l => l.agentId === activeLeadModalAgent.id || (activeLeadModalAgent.id === "default" && !l.agentId)).length === 0}
                style={{
                  background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                📥 Export to CSV
              </button>
            </div>

            {/* Leads Table Container */}
            <div style={{ flex: 1, overflowY: "auto", minHeight: "250px" }}>
              {(() => {
                const filtered = leadsList
                  .filter(l => l.agentId === activeLeadModalAgent.id || (activeLeadModalAgent.id === "default" && !l.agentId))
                  .filter(l => {
                    const search = modalLeadsSearch.toLowerCase();
                    return (
                      (l.name && l.name.toLowerCase().includes(search)) ||
                      (l.email && l.email.toLowerCase().includes(search)) ||
                      (l.phone && l.phone.toLowerCase().includes(search)) ||
                      (l.reason && l.reason.toLowerCase().includes(search))
                    );
                  });

                if (filtered.length === 0) {
                  return (
                    <div style={{ color: "#64748B", fontSize: "0.9rem", fontStyle: "italic", textAlign: "center", padding: "40px" }}>
                      No leads matching search or captured by this agent yet.
                    </div>
                  );
                }

                return (
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.85rem" }}>
                    <thead>
                      <tr style={{ borderBottom: "2px solid #E2E8F0", color: "#64748B" }}>
                        <th style={{ padding: "12px 8px", fontWeight: "700" }}>Name</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700" }}>Email</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700" }}>Phone</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700" }}>Reason / Inquiry</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700" }}>Date</th>
                        <th style={{ padding: "12px 8px", fontWeight: "700" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((lead) => (
                        <tr key={lead._id || lead.id} style={{ borderBottom: "1px solid #F1F5F9", color: "#1E293B" }}>
                          <td style={{ padding: "12px 8px", fontWeight: "600" }}>{lead.name}</td>
                          <td style={{ padding: "12px 8px", color: "#6366F1" }}>{lead.email}</td>
                          <td style={{ padding: "12px 8px", color: "#475569" }}>{lead.phone}</td>
                          <td style={{ padding: "12px 8px", color: "#64748B", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {lead.reason}
                          </td>
                          <td style={{ padding: "12px 8px", color: "#94A3B8" }}>{new Date(lead.createdAt).toLocaleDateString()}</td>
                          <td style={{ padding: "12px 8px" }}>
                            <button
                              onClick={() => setSelectedLeadForModal(lead)}
                              style={{
                                background: "#6366F1",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 10px",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                fontWeight: "600"
                              }}
                            >
                              🔍 View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            <div style={{ marginTop: "20px", borderTop: "1px solid #E2E8F0", paddingTop: "12px", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => { setActiveLeadModalAgent(null); setModalLeadsSearch(""); }}
                style={{
                  background: "#F1F5F9",
                  border: "none",
                  color: "#475569",
                  borderRadius: "8px",
                  padding: "8px 20px",
                  fontSize: "0.85rem",
                  fontWeight: "600",
                  cursor: "pointer"
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
